import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../Components/Button/Button";
import TextField from "../../Components/TextField/TextField";
import Dropdown from "../../Components/Dropdown/Dropdown";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import api from "../../services/api";
import readerApi from "../../services/readerApi";
import "./Operators.css";

const Operators = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Every scan-result TID pill is a link into the tag details page so a
  // user can drill in from a scan directly.
  const openTagDetails = (tid) => {
    if (!tid) return;
    navigate(`/tags/${encodeURIComponent(tid)}`);
  };

  // Tab state
  const [activeTab, setActiveTab] = useState("readers");

  // Warehouses from main backend (for reader assignment dropdown)
  const [warehouses, setWarehouses] = useState([]);

  // Readers state
  const [readers, setReaders] = useState([]);
  const [readersLoading, setReadersLoading] = useState(false);
  const [showReaderModal, setShowReaderModal] = useState(false);
  const [editingReader, setEditingReader] = useState(null);
  const [readerForm, setReaderForm] = useState({
    readerId: "",
    ip: "",
    port: "",
    antennaCount: "",
    warehouseLocation: "",
  });
  const [connectingReader, setConnectingReader] = useState(null);

  // Antennas state
  const [antennas, setAntennas] = useState([]);
  const [antennasLoading, setAntennasLoading] = useState(false);
  const [antennaFilterReader, setAntennaFilterReader] = useState("all");
  const [showAntennaModal, setShowAntennaModal] = useState(false);
  const [editingAntenna, setEditingAntenna] = useState(null);
  const [antennaForm, setAntennaForm] = useState({
    readerId: "",
    antennaNumber: "",
    usage: "",
    name: "",
    roomId: "",
    warehouseId: "",
    power: "30",
    // Relay a GATE antenna drives. Per-antenna so one reader can run several
    // gates on independent sirens. "2" matches the pin gates were hardwired
    // to before this became configurable.
    gpoPin: "2",
  });

  // Rooms from main backend (for scan results room names)
  const [rooms, setRooms] = useState([]);

  // Partition (room) → antennas map: derived from SCAN antennas (roomId → [antennaNumbers])
  const ridToAntennaMap = useMemo(() => {
    const map = {};
    for (const ant of antennas) {
      if (ant.usage === "SCAN" && ant.roomId) {
        if (!map[ant.roomId]) map[ant.roomId] = [];
        map[ant.roomId].push(ant.antennaNumber);
      }
    }
    // stable ordering for UI display
    for (const rid of Object.keys(map)) {
      map[rid] = Array.from(new Set(map[rid])).sort((a, b) => a - b);
    }
    return map;
  }, [antennas]);

  const getExpectedAntenna = (rid) => {
    const ants = ridToAntennaMap[rid];
    if (!ants || ants.length === 0) return "?";
    return ants.join(", ");
  };

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState(null);

  // Theft monitor state
  const [theftMonitors, setTheftMonitors] = useState([]);  // array of active readerIds
  const [togglingTheft, setTogglingTheft] = useState(null); // readerId currently toggling
  const [stoppingAlarm, setStoppingAlarm] = useState(null); // readerId whose alarm is being cleared
  // Local toggle state for the GPO2 wiring-test button — { readerId: bool }.
  // Tracks the LAST state the button sent to /test-gpo so the next click
  // sends the opposite. Defaults to off; rebuilt from the on/off click flow,
  // not from a server poll, so reloading the page resets it (which matches
  // user expectation for a test button).
  const [gpoTestOn, setGpoTestOn] = useState({});
  const [gpoTesting, setGpoTesting] = useState(null); // readerId currently in flight
  const [theftEvents, setTheftEvents] = useState([]);
  const [theftEventsLoading, setTheftEventsLoading] = useState(false);
  const [theftFilterAlarm, setTheftFilterAlarm] = useState(false);
  const [selectedTheftEvents, setSelectedTheftEvents] = useState(new Set());
  const [deletingTheftEvents, setDeletingTheftEvents] = useState(false);
  const theftPollRef = useRef(null);

  // Scanner state
  const [scanReader, setScanReader] = useState("");
  const [scanAntennas, setScanAntennas] = useState([]);
  const [scanDuration, setScanDuration] = useState("3");
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [continuousMode, setContinuousMode] = useState(false);
  const [scanPowerOverrides, setScanPowerOverrides] = useState({});
  const [localAntPower, setLocalAntPower] = useState({});
  const continuousPollRef = useRef(null);

  // System Scan state
  const [systemScanning, setSystemScanning] = useState(false);
  const [systemScanProgress, setSystemScanProgress] = useState(null); // { current, total, currentRoom }
  const [systemScanResults, setSystemScanResults] = useState(null); // array of per-room results
  const [systemScanDuration, setSystemScanDuration] = useState("3");
  const [systemContinuousMode, setSystemContinuousMode] = useState(false);
  const [systemScanExpandedRoom, setSystemScanExpandedRoom] = useState(null);
  const [systemScanAllTags, setSystemScanAllTags] = useState([]); // all enriched scanned tags
  const [systemScanTagCount, setSystemScanTagCount] = useState(0);
  const systemScanContextRef = useRef(null); // stores { readerIds, antennaRoomMap } for stop
  const systemContinuousPollRef = useRef(null);
  const [scannerPage, setScannerPage] = useState(1);
  const [systemTagsPage, setSystemTagsPage] = useState(1);
  const [systemRoomsPage, setSystemRoomsPage] = useState(1);
  const [theftEventsPage, setTheftEventsPage] = useState(1);
  const [scannerTidFilter, setScannerTidFilter] = useState("");
  const [systemTidFilter, setSystemTidFilter] = useState("");
  const [desktopDevices, setDesktopDevices] = useState([]);
  const [desktopUsbPaths, setDesktopUsbPaths] = useState([]);
  const [desktopSerialPorts, setDesktopSerialPorts] = useState([]);
  const [desktopLoading, setDesktopLoading] = useState(false);
  const [desktopSaving, setDesktopSaving] = useState(false);
  const [editingDesktopDevice, setEditingDesktopDevice] = useState(null);
  const [showDesktopModal, setShowDesktopModal] = useState(false);
  const [desktopForm, setDesktopForm] = useState({
    deviceId: "",
    name: "",
    usbPath: "",
    connectionType: "USB_HID",
    serialPort: "",
    baudRate: "115200",
    power: "30",
  });

  // Auto-refresh
  const refreshInterval = useRef(null);

  // ─── FETCH FUNCTIONS ────────────────────────────────────────

  const fetchWarehouses = useCallback(async () => {
    try {
      const data = await api.getWarehouses(1, 100);
      setWarehouses(data.warehouses || []);
    } catch (err) {
      console.error("Failed to fetch warehouses:", err);
    }
  }, []);

  const fetchRooms = useCallback(async () => {
    try {
      const data = await api.getRooms();
      setRooms(data.rooms || []);
    } catch (err) {
      console.error("Failed to fetch rooms:", err);
    }
  }, []);

  const fetchReaders = useCallback(async (silent = false) => {
    if (!silent) setReadersLoading(true);
    try {
      const data = await readerApi.getReaders();
      setReaders(data);
    } catch (err) {
      if (!silent) showToast("Failed to fetch readers", 3000, true, "danger");
    } finally {
      if (!silent) setReadersLoading(false);
    }
  }, [showToast]);

  const fetchTheftStatus = useCallback(async () => {
    try {
      const data = await readerApi.getTheftMonitorStatus();
      setTheftMonitors(data.activeMonitors || []);
    } catch (err) {
      console.error("Failed to fetch theft monitor status:", err);
    }
  }, []);

  const fetchTheftEvents = useCallback(async (silent = false) => {
    if (!silent) setTheftEventsLoading(true);
    try {
      const data = await readerApi.getTheftEvents(null, theftFilterAlarm);
      setTheftEvents(data.events || []);
    } catch (err) {
      if (!silent) console.error("Failed to fetch theft events:", err);
    } finally {
      if (!silent) setTheftEventsLoading(false);
    }
  }, [theftFilterAlarm]);

  const fetchAntennas = useCallback(async (silent = false) => {
    if (!silent) setAntennasLoading(true);
    try {
      const rid = antennaFilterReader === "all" ? undefined : antennaFilterReader;
      const data = await readerApi.getAntennas(rid);
      setAntennas(data);
      setLocalAntPower({});
    } catch (err) {
      if (!silent) showToast("Failed to fetch antennas", 3000, true, "danger");
    } finally {
      if (!silent) setAntennasLoading(false);
    }
  }, [antennaFilterReader, showToast]);

  const fetchDesktopDevices = useCallback(async () => {
    setDesktopLoading(true);
    try {
      const data = await readerApi.getDesktopDevices();
      setDesktopDevices(data.devices || []);
    } catch (err) {
      showToast(err.message || "Failed to fetch desktop devices", 3000, true, "danger");
    } finally {
      setDesktopLoading(false);
    }
  }, [showToast]);

  const fetchDesktopUsbPaths = useCallback(async () => {
    try {
      const data = await readerApi.getDesktopUsbPaths();
      setDesktopUsbPaths(data.paths || []);
    } catch (err) {
      showToast(err.message || "Failed to list USB paths", 3000, true, "danger");
    }
  }, [showToast]);

  const fetchDesktopSerialPorts = useCallback(async () => {
    try {
      const data = await readerApi.getDesktopSerialPorts();
      setDesktopSerialPorts(data.ports || []);
    } catch (err) {
      showToast(err.message || "Failed to list COM ports", 3000, true, "danger");
    }
  }, [showToast]);

  // Fetch warehouses, rooms, and theft status on mount
  useEffect(() => {
    fetchWarehouses();
    fetchRooms();
    fetchTheftStatus();
  }, [fetchWarehouses, fetchRooms, fetchTheftStatus]);

  // Initial + tab-based fetching
  useEffect(() => {
    if (activeTab === "readers") fetchReaders();
    if (activeTab === "antennas") fetchAntennas();
    if (activeTab === "scanner" || activeTab === "systemScan") {
      fetchReaders(true);
      fetchAntennas(true);
    }
    if (activeTab === "theft") {
      fetchReaders(true);
      fetchAntennas(true);
      fetchTheftStatus();
      fetchTheftEvents();
    }
    if (activeTab === "desktopDevices") {
      fetchDesktopDevices();
      fetchDesktopUsbPaths();
      fetchDesktopSerialPorts();
    }
  }, [activeTab, fetchReaders, fetchAntennas, fetchTheftStatus, fetchTheftEvents, fetchDesktopDevices, fetchDesktopUsbPaths, fetchDesktopSerialPorts]);

  // Auto-refresh theft tab every 2s (status + readers). Detection events
  // themselves arrive live through the SSE stream below, so we no longer
  // poll /theft-events on this interval.
  useEffect(() => {
    if (activeTab === "theft") {
      theftPollRef.current = setInterval(() => {
        fetchTheftStatus();
        fetchReaders(true);
      }, 2000);
      return () => clearInterval(theftPollRef.current);
    }
  }, [activeTab, fetchTheftStatus, fetchReaders]);

  // Live Detection-log stream (Server-Sent Events).  The Python backend
  // pushes a JSON event the instant _log_theft_event inserts into Mongo,
  // so READY→ON_DELIVERY transitions and gate check-ins appear in the UI
  // with no polling lag.  We still run fetchTheftEvents on mount and when
  // the filter changes to hydrate the list; SSE only delivers new events.
  useEffect(() => {
    if (activeTab !== "theft") return undefined;
    let es;
    try {
      es = new EventSource(readerApi.theftEventsStreamUrl());
    } catch (err) {
      console.error("[THEFT SSE] Failed to open stream:", err);
      return undefined;
    }
    es.onmessage = (ev) => {
      if (!ev?.data) return;
      let evt;
      try { evt = JSON.parse(ev.data); }
      catch (err) { console.warn("[THEFT SSE] bad payload", err); return; }
      // If the alarm filter is on, drop non-alarm events client-side
      // so they don't appear and then disappear on the next refresh.
      if (theftFilterAlarm && !evt.alarm) return;
      setTheftEvents((prev) => {
        // Dedupe by _id in case a re-render replays a just-fetched event
        if (evt._id && prev.some((e) => e._id === evt._id)) return prev;
        return [evt, ...prev].slice(0, 500);
      });
    };
    es.onerror = (err) => {
      // Browser auto-reconnects — just log for diagnostics
      console.warn("[THEFT SSE] stream error (will retry):", err);
    };
    return () => {
      try { es.close(); } catch { /* noop */ }
    };
  }, [activeTab, theftFilterAlarm]);

  // Re-fetch theft events when filter changes
  useEffect(() => {
    if (activeTab === "theft") fetchTheftEvents();
  }, [theftFilterAlarm, activeTab, fetchTheftEvents]);

  useEffect(() => {
    setTheftEventsPage(1);
  }, [theftFilterAlarm]);

  useEffect(() => {
    setTheftEventsPage((prev) => Math.min(prev, Math.max(1, Math.ceil(theftEvents.length / 20))));
  }, [theftEvents.length]);

  // Reset pagination whenever the Tag ID filter changes so results start on page 1.
  useEffect(() => { setScannerPage(1); }, [scannerTidFilter]);
  useEffect(() => { setSystemTagsPage(1); }, [systemTidFilter]);

  // Cleanup continuous scan poll on unmount
  useEffect(() => {
    return () => {
      if (continuousPollRef.current) clearInterval(continuousPollRef.current);
      if (theftPollRef.current) clearInterval(theftPollRef.current);
      if (systemContinuousPollRef.current) clearInterval(systemContinuousPollRef.current);
    };
  }, []);

  // Auto-refresh readers + theft status every 5s when on readers tab
  useEffect(() => {
    if (activeTab === "readers") {
      refreshInterval.current = setInterval(() => {
        fetchReaders(true);
        fetchTheftStatus();
      }, 5000);
      return () => clearInterval(refreshInterval.current);
    }
  }, [activeTab, fetchReaders, fetchTheftStatus]);

  // Re-fetch antennas when filter changes
  useEffect(() => {
    if (activeTab === "antennas") fetchAntennas();
  }, [antennaFilterReader, activeTab, fetchAntennas]);

  // ─── READER ACTIONS ─────────────────────────────────────────

  const handleAddReader = async () => {
    const { readerId, ip, port, antennaCount, warehouseLocation } = readerForm;
    if (!readerId || !ip || !port || !antennaCount || !warehouseLocation) {
      showToast("All fields are required", 3000, true, "danger");
      return;
    }
    try {
      await readerApi.assignReader({
        readerId,
        ip,
        port: parseInt(port),
        antennaCount: parseInt(antennaCount),
        warehouseLocation,
      });
      showToast(editingReader ? "Reader updated successfully" : "Reader assigned successfully", 3000, true, "success");
      setShowReaderModal(false);
      setEditingReader(null);
      setReaderForm({ readerId: "", ip: "", port: "", antennaCount: "", warehouseLocation: "" });
      fetchReaders();
    } catch (err) {
      showToast(err.message || "Failed to save reader", 3000, true, "danger");
    }
  };

  const handleConnect = async (readerId) => {
    setConnectingReader(readerId);
    try {
      const res = await readerApi.connectReader(readerId);
      showToast(res.message || "Connected", 3000, true, "success");
      fetchReaders();
    } catch (err) {
      showToast(err.message || "Connection failed", 3000, true, "danger");
    } finally {
      setConnectingReader(null);
    }
  };

  const handleDisconnect = async (readerId) => {
    setConnectingReader(readerId);
    try {
      await readerApi.disconnectReader(readerId);
      showToast("Disconnected", 3000, true, "success");
      fetchReaders();
    } catch (err) {
      showToast(err.message || "Disconnect failed", 3000, true, "danger");
    } finally {
      setConnectingReader(null);
    }
  };

  const handleStopAlarm = async (readerId) => {
    setStoppingAlarm(readerId);
    try {
      await readerApi.stopAlarm(readerId);
      showToast(`Alarm cleared on ${readerId}`, 3000, true, "success");
      fetchReaders();
    } catch (err) {
      showToast(err.message || "Failed to stop alarm", 3000, true, "danger");
    } finally {
      setStoppingAlarm(null);
    }
  };

  // Alarm pins actually configured on a reader's GATE antennas.  Gates now
  // choose their own GPO, so testing a hardcoded GPO2 would rattle the wrong
  // relay — and report success while the operator's siren stayed silent.
  // Falls back to [2] for readers whose gates predate the `gpoPin` field,
  // which is exactly what the backend defaults them to.
  const getReaderGpoPins = (readerId) => {
    const pins = antennas
      .filter((a) => a.readerId === readerId
        && (a.usage === "GATE" || a.usage === "THEFT" || a.usage === "CHECKING"))
      .map((a) => Number(a.gpoPin) || 2);
    const unique = Array.from(new Set(pins)).sort((x, y) => x - y);
    return unique.length > 0 ? unique : [2];
  };

  // Toggle a reader's alarm relay(s) for wiring verification.  Click once to
  // close (siren / Sonoff trigger ON), click again to open (OFF).  State is
  // local — a page reload resets the button to "off".  A reader running two
  // gates on different pins toggles both, so one click proves the whole
  // alarm path regardless of which gate fires it.
  const handleTestGpo = async (readerId) => {
    if (gpoTesting === readerId) return;
    const pins = getReaderGpoPins(readerId);
    const currentlyOn = !!gpoTestOn[readerId];
    const desired = currentlyOn ? 0 : 1;
    const label = pins.map((p) => `GPO${p}`).join(" + ");
    setGpoTesting(readerId);
    try {
      // Sequential, not Promise.all: these are synchronous SDK sends on the
      // reader's single command lock, so firing them together just queues
      // them behind each other with less legible errors.
      for (const pin of pins) {
        await readerApi.testGpo(readerId, pin, desired);
      }
      setGpoTestOn(prev => ({ ...prev, [readerId]: !currentlyOn }));
      showToast(
        desired === 1
          ? `${label} closed on ${readerId} — siren / load ON`
          : `${label} opened on ${readerId} — siren / load OFF`,
        2000, true, desired === 1 ? "success" : "info"
      );
    } catch (err) {
      showToast(err.message || `Failed to set ${label}=${desired} on ${readerId}`, 3000, true, "danger");
    } finally {
      setGpoTesting(null);
    }
  };

  const handleSaveDesktopDevice = async () => {
    const payload = {
      deviceId: desktopForm.deviceId.trim(),
      name: desktopForm.name.trim(),
      usbPath: desktopForm.usbPath.trim(),
      connectionType: desktopForm.connectionType,
      serialPort: (desktopForm.serialPort || "").trim().toUpperCase(),
      baudRate: parseInt(desktopForm.baudRate || "115200"),
      power: parseInt(desktopForm.power || "30"),
      allowUpdate: !!editingDesktopDevice,
    };
    if (!payload.deviceId || !payload.name) {
      showToast("Device ID and Name are required", 3000, true, "danger");
      return;
    }
    if (payload.connectionType === "USB_HID" && !payload.usbPath) {
      showToast("USB HID path is required for USB_HID mode", 3000, true, "danger");
      return;
    }
    if (payload.connectionType === "SERIAL" && !payload.serialPort) {
      showToast("COM Port is required for SERIAL mode", 3000, true, "danger");
      return;
    }
    if (Number.isNaN(payload.power) || payload.power < 5 || payload.power > 33) {
      showToast("Power must be between 5 and 33 dBm", 3000, true, "danger");
      return;
    }
    if (!editingDesktopDevice) {
      const duplicateId = desktopDevices.some((d) => (d.deviceId || "").toUpperCase() === payload.deviceId.toUpperCase());
      if (duplicateId) {
        showToast("A desktop POS device with this ID already exists", 3000, true, "danger");
        return;
      }
      if (payload.connectionType === "SERIAL") {
        const duplicateCom = desktopDevices.some(
          (d) =>
            (d.connectionType || "").toUpperCase() === "SERIAL" &&
            (d.serialPort || "").toUpperCase() === payload.serialPort.toUpperCase()
        );
        if (duplicateCom) {
          showToast("This COM port is already assigned to another POS device", 3000, true, "danger");
          return;
        }
      } else {
        const duplicateUsb = desktopDevices.some(
          (d) =>
            (d.connectionType || "").toUpperCase() === "USB_HID" &&
            (d.usbPath || "").trim() === payload.usbPath
        );
        if (duplicateUsb) {
          showToast("This USB HID path is already assigned to another POS device", 3000, true, "danger");
          return;
        }
      }
    }
    setDesktopSaving(true);
    try {
      await readerApi.upsertDesktopDevice(payload);
      showToast(editingDesktopDevice ? "Desktop device updated" : "Desktop device saved", 2500, true, "success");
      setEditingDesktopDevice(null);
      setShowDesktopModal(false);
      setDesktopForm({ deviceId: "", name: "", usbPath: "", connectionType: "USB_HID", serialPort: "", baudRate: "115200", power: "30" });
      fetchDesktopDevices();
    } catch (err) {
      showToast(err.message || "Failed to save desktop device", 3000, true, "danger");
    } finally {
      setDesktopSaving(false);
    }
  };

  const handleDeleteDesktopDevice = async (deviceId) => {
    setConfirmModal({
      title: "Delete Desktop Device",
      message: `Delete desktop device "${deviceId}"?`,
      detail: "This also stops any running POS session for that device.",
      confirmLabel: "Delete Device",
      icon: "fa-trash",
      onConfirm: async () => {
        setConfirmModal(null);
        await readerApi.deleteDesktopDevice(deviceId);
        showToast("Desktop device deleted", 2500, true, "success");
        fetchDesktopDevices();
      },
      danger: true,
    });
  };

  const handleEditDesktopDevice = (device) => {
    setEditingDesktopDevice(device.deviceId);
    setDesktopForm({
      deviceId: device.deviceId || "",
      name: device.name || "",
      usbPath: device.usbPath || "",
      connectionType: device.connectionType || "USB_HID",
      serialPort: device.serialPort || "",
      baudRate: String(device.baudRate || 115200),
      power: String(device.power || 30),
    });
    setShowDesktopModal(true);
  };

  const handleCancelDesktopEdit = () => {
    setEditingDesktopDevice(null);
    setShowDesktopModal(false);
    setDesktopForm({
      deviceId: "",
      name: "",
      usbPath: "",
      connectionType: "USB_HID",
      serialPort: "",
      baudRate: "115200",
      power: "30",
    });
  };

  const handleOpenDesktopCreate = () => {
    setEditingDesktopDevice(null);
    setDesktopForm({
      deviceId: "",
      name: "",
      usbPath: "",
      connectionType: "USB_HID",
      serialPort: "",
      baudRate: "115200",
      power: "30",
    });
    setShowDesktopModal(true);
  };

  const handleToggleTheftMonitor = async (readerId) => {
    const isActive = theftMonitors.includes(readerId);
    setTogglingTheft(readerId);
    try {
      if (isActive) {
        await readerApi.stopTheftMonitor(readerId);
        showToast(`Theft monitor paused for ${readerId}`, 3000, true, "success");
      } else {
        const res = await readerApi.startTheftMonitor(readerId);
        showToast(res.message || `Theft monitor resumed for ${readerId}`, 3000, true, "success");
      }
      // Await refresh so the UI state is updated before we release the button
      await fetchTheftStatus();
      await fetchTheftEvents(true);
      await fetchReaders();
    } catch (err) {
      console.error("Toggle theft monitor error:", err);
      showToast(err.message || "Failed to toggle theft monitor", 3000, true, "danger");
      // Still refresh status so UI stays in sync
      await fetchTheftStatus();
    } finally {
      setTogglingTheft(null);
    }
  };

  const handleClearTheftEvents = () => {
    setConfirmModal({
      title: "Clear Theft Log",
      message: "Are you sure you want to clear all theft detection events?",
      detail: "This will permanently remove the entire theft event history.",
      confirmLabel: "Clear All",
      icon: "fa-trash",
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await readerApi.clearTheftEvents();
          showToast("Theft events cleared", 3000, true, "success");
          setSelectedTheftEvents(new Set());
          fetchTheftEvents();
          fetchTheftStatus();
        } catch (err) {
          showToast(err.message || "Failed to clear events", 3000, true, "danger");
        }
      },
    });
  };

  const handleDeleteSelectedTheftEvents = () => {
    if (selectedTheftEvents.size === 0) return;
    const count = selectedTheftEvents.size;
    setConfirmModal({
      title: "Delete Selected Events",
      message: `Are you sure you want to delete ${count} selected event(s)?`,
      detail: "Deleted tags will be unlocked for re-detection by the theft monitor.",
      confirmLabel: `Delete ${count} Event(s)`,
      icon: "fa-trash",
      onConfirm: async () => {
        setConfirmModal(null);
        setDeletingTheftEvents(true);
        try {
          await readerApi.deleteTheftEventsBatch([...selectedTheftEvents]);
          showToast(`${count} event(s) deleted`, 3000, true, "success");
          setSelectedTheftEvents(new Set());
          fetchTheftEvents();
        } catch (err) {
          showToast(err.message || "Failed to delete events", 3000, true, "danger");
        } finally {
          setDeletingTheftEvents(false);
        }
      },
    });
  };

  const toggleTheftEventSelection = (eventId) => {
    setSelectedTheftEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const toggleSelectAllTheftEvents = () => {
    const pageEvents = pagedTheftEvents.filter((e) => e?._id);
    const pageIds = pageEvents.map((e) => e._id);
    const allSelectedOnPage = pageIds.length > 0 && pageIds.every((id) => selectedTheftEvents.has(id));
    if (allSelectedOnPage) {
      setSelectedTheftEvents((prev) => {
        const next = new Set(prev);
        for (const id of pageIds) next.delete(id);
        return next;
      });
    } else {
      setSelectedTheftEvents((prev) => {
        const next = new Set(prev);
        for (const id of pageIds) next.add(id);
        return next;
      });
    }
  };

  const handleDeleteReader = (readerId) => {
    setConfirmModal({
      title: "Delete Reader",
      message: `Are you sure you want to delete reader "${readerId}"?`,
      detail: "This will permanently remove this reader and all its antenna assignments.",
      confirmLabel: "Delete Reader",
      icon: "fa-server",
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await readerApi.deleteReader(readerId);
          showToast("Reader deleted", 3000, true, "success");
          fetchReaders();
          fetchAntennas(true);
        } catch (err) {
          showToast(err.message || "Failed to delete reader", 3000, true, "danger");
        }
      },
    });
  };

  // ─── ANTENNA ACTIONS ────────────────────────────────────────

  const handleAssignAntenna = async () => {
    const { readerId, antennaNumber, usage, name, roomId, power, gpoPin } = antennaForm;
    if (!readerId || !antennaNumber || !usage) {
      showToast("Reader, antenna number, and usage are required", 3000, true, "danger");
      return;
    }
    if (usage === "SCAN" && !roomId) {
      showToast("Partition is required for SCAN antennas", 3000, true, "danger");
      return;
    }
    // GATE antennas inherit their warehouse from the reader, so we pre-flight
    // the reader has one set before letting the user submit.
    const isGateUsage = usage === "GATE" || usage === "THEFT" || usage === "CHECKING";
    if (isGateUsage) {
      const reader = readers.find((r) => r.readerId === readerId);
      if (!reader?.warehouseLocation && !reader?.warehouseId) {
        showToast(
          "Assign a warehouse to this reader before creating a GATE antenna.",
          4000, true, "danger"
        );
        return;
      }
    }
    try {
      await readerApi.assignAntenna({
        readerId,
        antennaNumber: parseInt(antennaNumber),
        usage,
        name: name || undefined,
        roomId: usage === "SCAN" && roomId ? parseInt(roomId) : undefined,
        // GATE/THEFT/CHECKING omit warehouseId — server uses reader.warehouseId.
        power: parseInt(power) || 30,
        // Only meaningful for gates; the server clears any stale pin on SCAN.
        gpoPin: isGateUsage ? parseInt(gpoPin) || 2 : undefined,
      });
      showToast(editingAntenna ? "Antenna updated successfully" : "Antenna assigned successfully", 3000, true, "success");
      setShowAntennaModal(false);
      setEditingAntenna(null);
      setAntennaForm({ readerId: "", antennaNumber: "", usage: "", name: "", roomId: "", warehouseId: "", power: "30", gpoPin: "2" });
      fetchAntennas();
    } catch (err) {
      showToast(err.message || "Failed to save antenna", 3000, true, "danger");
    }
  };

  const handleDeleteAntenna = (readerId, antennaNumber) => {
    setConfirmModal({
      title: "Delete Antenna",
      message: `Are you sure you want to delete Antenna ${antennaNumber} from reader "${readerId}"?`,
      detail: "This antenna assignment will be permanently removed.",
      confirmLabel: "Delete Antenna",
      icon: "fa-wifi",
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await readerApi.deleteAntenna(readerId, antennaNumber);
          showToast("Antenna deleted", 3000, true, "success");
          fetchAntennas();
        } catch (err) {
          showToast(err.message || "Failed to delete antenna", 3000, true, "danger");
        }
      },
    });
  };

  // ─── SCANNER ACTIONS ────────────────────────────────────────

  const normalizeTags = (tags) =>
    (tags || []).map((t) => ({
      tid: t.tid || "",
      epc: t.epc || "",
      name: t.name || "",
      state: t.state || "UNKNOWN",
      antenna: t.antenna || 0,
      rssi: t.rssi || 0,
      rid: t.rid || 0,
      registered: t.registered !== false,
      timestamp: t.timestamp || null,
      ...t,
    }));

  const handleScan = async () => {
    if (!scanReader) {
      showToast("Select a reader first", 3000, true, "danger");
      return;
    }
    if (scanAntennas.length === 0) {
      showToast("Select at least one antenna", 3000, true, "danger");
      return;
    }
    setScanning(true);
    setScanResults(null);
    setScannerPage(1);

    const antennaNums = scanAntennas.map((a) => parseInt(a));

    // Build power overrides (only include overrides that differ from default)
    const overrides = Object.keys(scanPowerOverrides).length > 0 ? scanPowerOverrides : null;

    if (continuousMode) {
      // Start continuous scan — results come when we stop
      try {
        await readerApi.startContinuousScan(scanReader, antennaNums, overrides);
        showToast("Continuous scan started — click Stop to finish", 4000, true, "success");
        // Poll for live tag count
        continuousPollRef.current = setInterval(async () => {
          try {
            const status = await readerApi.getContinuousScanStatus(scanReader);
            if (status.running) {
              setScanResults((prev) => ({
                ...(prev || {}),
                tagsFound: status.tagsFound,
                tags: prev?.tags || [],
                continuous: true,
              }));
            }
          } catch {
            // ignore poll errors
          }
        }, 2000);
      } catch (err) {
        showToast(err.message || "Failed to start continuous scan", 3000, true, "danger");
        setScanning(false);
      }
    } else {
      // Duration-based scan
      try {
        const res = await readerApi.scanAntennas(scanReader, antennaNums, parseInt(scanDuration), overrides);
        const tags = normalizeTags(res.tags);
        setScanResults({ ...res, tags });
        showToast(`Scan complete - ${res.tagsFound} tag(s) found`, 3000, true, "success");
      } catch (err) {
        showToast(err.message || "Scan failed", 3000, true, "danger");
      } finally {
        setScanning(false);
      }
    }
  };

  const handleStopScan = async () => {
    // Clear poll interval
    if (continuousPollRef.current) {
      clearInterval(continuousPollRef.current);
      continuousPollRef.current = null;
    }
    try {
      const res = await readerApi.stopContinuousScan(scanReader);
      const tags = normalizeTags(res.tags);
      setScanResults({ ...res, tags, continuous: false });
      setScannerPage(1);
      showToast(`Scan stopped - ${res.tagsFound} tag(s) found`, 3000, true, "success");
    } catch (err) {
      showToast(err.message || "Failed to stop scan", 3000, true, "danger");
    } finally {
      setScanning(false);
    }
  };

  // ─── SYSTEM SCAN ACTION ─────────────────────────────────────

  // Shared: discover readers + SCAN antennas, return context or null on failure
  const _prepareSystemScan = async () => {
    const allReaders = await readerApi.getReaders();
    const allAntennas = await readerApi.getAntennas();
    const connectedReaders = allReaders.filter((r) => r.connected);

    if (connectedReaders.length === 0) {
      showToast("No connected readers found", 3000, true, "danger");
      return null;
    }

    const scanAntennasByReader = {};
    const antennaRoomMap = {};
    for (const ant of allAntennas) {
      if (ant.usage !== "SCAN") continue;
      if (!connectedReaders.find((r) => r.readerId === ant.readerId)) continue;
      if (!ant.roomId) continue;
      if (!scanAntennasByReader[ant.readerId]) scanAntennasByReader[ant.readerId] = [];
      scanAntennasByReader[ant.readerId].push(ant.antennaNumber);
      antennaRoomMap[`${ant.readerId}_${ant.antennaNumber}`] = ant.roomId;
    }

    const readerIds = Object.keys(scanAntennasByReader);
    if (readerIds.length === 0) {
      showToast("No SCAN antennas assigned to connected readers", 3000, true, "danger");
      return null;
    }
    return { readerIds, scanAntennasByReader, antennaRoomMap };
  };

  // Shared: determine which room a scanned tag belongs to.
  // Groups by physical antenna detection: a tag only counts as "found"
  // in a room if that room's antenna actually detected it.
  const _getTagRoom = (tag, readerId, antennaRoomMap) => {
    // Primary: antenna → room mapping (physical detection)
    const antNum = tag.antenna || 0;
    if (antNum > 0) {
      const roomByAntenna = antennaRoomMap[`${readerId}_${antNum}`];
      if (roomByAntenna) return roomByAntenna;
    }
    // Fallback for antenna=0 (hardware didn't report which antenna):
    // if only one SCAN antenna on this reader, it must be that one
    const readerAntennas = Object.keys(antennaRoomMap)
      .filter((k) => k.startsWith(`${readerId}_`));
    if (readerAntennas.length === 1) return antennaRoomMap[readerAntennas[0]];
    return null;
  };

  /**
   * Core processing: group raw scanned tags by room, compare each room
   * with the database, then cross-reference to categorise every tag as
   * matched / missing / misplaced / extra / not-registered.
   *
   * @param {Array<{tag: Object, readerId: string}>} rawTagEntries
   * @param {Object} ctx - from _prepareSystemScan
   * @returns {{ roomResults: Array, allScannedTags: Array }}
   */
  const _processSystemScanResults = async (rawTagEntries, ctx) => {
    // ── 0. Global de-dup: one hit per (tid, epc). TID is unique per physical
    // chip, so two tags sharing an EPC are kept as separate entries. Re-reads
    // of the same physical chip still collapse into one. Tags without a TID
    // fall back to EPC-only collapsing (unavoidable — the reader gave us
    // nothing to distinguish them).
    const uniqueByKey = new Map();
    for (const entry of rawTagEntries) {
      const epc = (entry?.tag?.epc || "").trim().toUpperCase();
      const tid = (entry?.tag?.tid || "").trim().toUpperCase();
      if (!epc && !tid) continue;
      const dedupKey = `${tid}|${epc}`;

      const roomId = _getTagRoom(entry.tag, entry.readerId, ctx.antennaRoomMap);
      const rssi = Number(entry?.tag?.rssi ?? -9999);
      const current = uniqueByKey.get(dedupKey);

      if (!current) {
        uniqueByKey.set(dedupKey, { ...entry, roomId, _rssi: rssi });
        continue;
      }

      // Prefer physically mapped room hits; if tie, prefer stronger RSSI.
      const currentRoomScore = current.roomId ? 1 : 0;
      const nextRoomScore = roomId ? 1 : 0;
      const currentRssi = Number(current._rssi ?? -9999);
      if (nextRoomScore > currentRoomScore || (nextRoomScore === currentRoomScore && rssi > currentRssi)) {
        uniqueByKey.set(dedupKey, { ...entry, roomId, _rssi: rssi });
      }
    }
    const uniqueEntries = Array.from(uniqueByKey.values());

    // ── 1. Group tags by room based on physical antenna ──────────
    const allScannedTags = [];
    const tagsByRoom = {};

    // Pre-fill every mapped room so rooms with 0 scanned tags appear
    for (const key of Object.keys(ctx.antennaRoomMap)) {
      const rid = ctx.antennaRoomMap[key];
      if (!tagsByRoom[rid]) tagsByRoom[rid] = [];
    }

    for (const { tag, roomId } of uniqueEntries) {
      allScannedTags.push(normalizeTags([tag])[0]);
      if (roomId) {
        if (!tagsByRoom[roomId]) tagsByRoom[roomId] = [];
        tagsByRoom[roomId].push(tag);
      }
    }

    // ── 2. Compare each room's scanned tags with DB (parallel) ──
    const roomIds = Object.keys(tagsByRoom);
    const compareConcurrency = 4;
    const roomResults = [];

    for (let i = 0; i < roomIds.length; i += compareConcurrency) {
      const batch = roomIds.slice(i, i + compareConcurrency);
      const batchResults = await Promise.all(batch.map(async (rid) => {
        const scannedTags = tagsByRoom[rid];
        const payload = scannedTags.map((t) => ({
          epc: t.epc || "",
          tid: t.tid || "",
          antenna: t.antenna || 0,
          rssi: t.rssi || 0,
        }));

        try {
          const result = await api.compareTags(payload, rid);
          return {
            roomId: parseInt(rid),
            roomName: getRoomName(rid),
            scannedCount: scannedTags.length,
            ...result,
          };
        } catch (err) {
          return {
            roomId: parseInt(rid),
            roomName: getRoomName(rid),
            scannedCount: scannedTags.length,
            error: err.message,
          };
        }
      }));

      roomResults.push(...batchResults);
    }

    // ── 3. Build lookup: where was each EPC physically scanned? ─
    const scannedLocationByEpc = {};
    for (const { tag, readerId, roomId } of uniqueEntries) {
      const epc = (tag.epc || "").trim().toUpperCase();
      if (!epc) continue;
      if (!roomId) continue;
      scannedLocationByEpc[epc] = {
        roomId: parseInt(roomId),
        roomName: getRoomName(roomId),
        readerId,
        antenna: tag.antenna || 0,
      };
    }

    // ── 4. Split missing → truly missing + misplaced; annotate extra
    for (const room of roomResults) {
      if (room.error) continue;

      const trulyMissing = [];
      const misplaced = [];
      for (const tag of (room.missing?.tags || [])) {
        const epc = (tag.epc || "").trim().toUpperCase();
        const found = scannedLocationByEpc[epc];
        if (found && found.roomId !== room.roomId) {
          misplaced.push({
            ...tag,
            detectedRoomId: found.roomId,
            detectedRoomName: found.roomName,
            detectedReaderId: found.readerId,
            detectedAntenna: found.antenna,
          });
        } else {
          trulyMissing.push(tag);
        }
      }
      room.missing = { count: trulyMissing.length, tags: trulyMissing };
      room.misplaced = { count: misplaced.length, tags: misplaced };
      if (room.summary) {
        room.summary.missingCount = trulyMissing.length;
        room.summary.misplacedCount = misplaced.length;
      }

      // Annotate extra tags with their DB room name
      for (const tag of (room.extra?.tags || [])) {
        if (tag.registered !== false && tag.dbRoomId) {
          tag.belongsToRoomName = getRoomName(tag.dbRoomId);
        }
      }
    }

    // ── 5. Determine registered status for every scanned tag ────
    const registeredEpcs = new Set();
    for (const room of roomResults) {
      if (room.error) continue;
      for (const t of (room.alreadyExists?.tags || []))
        registeredEpcs.add((t.epc || "").trim().toUpperCase());
      for (const t of (room.missing?.tags || []))
        registeredEpcs.add((t.epc || "").trim().toUpperCase());
      for (const t of (room.misplaced?.tags || []))
        registeredEpcs.add((t.epc || "").trim().toUpperCase());
      for (const t of (room.extra?.tags || []))
        if (t.registered !== false) registeredEpcs.add((t.epc || "").trim().toUpperCase());
    }

    for (const tag of allScannedTags) {
      tag.registered = registeredEpcs.has((tag.epc || "").trim().toUpperCase());
    }

    return { roomResults, allScannedTags };
  };

  const _showCompareToast = (roomResults, allTags) => {
    const totalMatched = roomResults.reduce((s, r) => s + (r.summary?.alreadyExistsCount || 0), 0);
    const totalMissing = roomResults.reduce((s, r) => s + (r.summary?.missingCount || 0), 0);
    const totalMisplaced = roomResults.reduce((s, r) => s + (r.summary?.misplacedCount || 0), 0);
    const totalExtra = roomResults.reduce((s, r) => s + (r.summary?.extraCount || 0), 0);
    const totalUnregistered = allTags ? allTags.filter((t) => t.registered === false).length : 0;
    const parts = [`${totalMatched} matched`];
    if (totalMissing > 0) parts.push(`${totalMissing} missing`);
    if (totalMisplaced > 0) parts.push(`${totalMisplaced} misplaced`);
    if (totalExtra > 0) parts.push(`${totalExtra} extra`);
    if (totalUnregistered > 0) parts.push(`${totalUnregistered} not registered`);
    showToast(
      `System scan complete: ${parts.join(", ")}`,
      5000, true, totalMissing > 0 || totalMisplaced > 0 || totalExtra > 0 ? "warning" : "success"
    );
  };

  // ── Duration-based system scan ──
  const handleSystemScan = async () => {
    setSystemScanning(true);
    setSystemScanResults(null);
    setSystemScanProgress(null);
    setSystemScanExpandedRoom(null);
    setSystemScanAllTags([]);
    setSystemScanTagCount(0);
    setSystemTagsPage(1);
    setSystemRoomsPage(1);

    try {
      const ctx = await _prepareSystemScan();
      if (!ctx) { setSystemScanning(false); return; }

      if (systemContinuousMode) {
        // ── Continuous mode: start all readers, show Stop button ──
        systemScanContextRef.current = ctx;
        setSystemScanProgress({ current: 0, total: ctx.readerIds.length, currentRoom: "Starting continuous scans..." });

        for (let i = 0; i < ctx.readerIds.length; i++) {
          const readerId = ctx.readerIds[i];
          const antNums = ctx.scanAntennasByReader[readerId];
          setSystemScanProgress({ current: i + 1, total: ctx.readerIds.length, currentRoom: `Starting ${readerId} (antennas ${antNums.join(", ")})` });
          try {
            await readerApi.startContinuousScan(readerId, antNums);
          } catch (err) {
            showToast(`Failed to start scan on ${readerId}: ${err.message}`, 3000, true, "danger");
          }
        }

        setSystemScanProgress(null);
        showToast("Continuous scan started — click Stop to finish and compare", 4000, true, "success");

        systemContinuousPollRef.current = setInterval(async () => {
          try {
            const status = await readerApi.getContinuousScanStatus();
            if (typeof status.totalUniqueTags === "number") {
              setSystemScanTagCount(status.totalUniqueTags);
              return;
            }
            // Backward-compat fallback
            const total = Object.values(status.activeScans || {}).reduce((sum, count) => sum + (count || 0), 0);
            setSystemScanTagCount(total);
          } catch { /* ignore poll errors */ }
        }, 2000);

        return; // stay in scanning state until Stop is pressed
      }

      // ── Duration-based mode: scan each reader, collect raw tags ──
      const rawTagEntries = [];
      for (let i = 0; i < ctx.readerIds.length; i++) {
        const readerId = ctx.readerIds[i];
        const antNums = ctx.scanAntennasByReader[readerId];
        setSystemScanProgress({ current: i + 1, total: ctx.readerIds.length + 1, currentRoom: `Scanning ${readerId} (antennas ${antNums.join(", ")})` });
        try {
          const res = await readerApi.scanAntennas(readerId, antNums, parseInt(systemScanDuration));
          for (const tag of (res.tags || [])) rawTagEntries.push({ tag, readerId });
        } catch (err) {
          showToast(`Scan failed on ${readerId}: ${err.message}`, 3000, true, "danger");
        }
      }

      setSystemScanProgress({ current: ctx.readerIds.length + 1, total: ctx.readerIds.length + 1, currentRoom: "Comparing with database..." });

      const { roomResults, allScannedTags } = await _processSystemScanResults(rawTagEntries, ctx);
      setSystemScanAllTags(allScannedTags);
      setSystemScanResults(roomResults);
    setSystemTagsPage(1);
    setSystemRoomsPage(1);
      setSystemScanProgress(null);
      _showCompareToast(roomResults, allScannedTags);
    } catch (err) {
      showToast(err.message || "System scan failed", 3000, true, "danger");
    } finally {
      if (!systemContinuousMode) {
        setSystemScanning(false);
        setSystemScanProgress(null);
      }
    }
  };

  // ── Stop continuous system scan ──
  const handleStopSystemScan = async () => {
    if (systemContinuousPollRef.current) {
      clearInterval(systemContinuousPollRef.current);
      systemContinuousPollRef.current = null;
    }

    const ctx = systemScanContextRef.current;
    if (!ctx) { setSystemScanning(false); return; }

    setSystemScanProgress({ current: 0, total: ctx.readerIds.length + 1, currentRoom: "Stopping scans..." });

    const rawTagEntries = [];
    for (let i = 0; i < ctx.readerIds.length; i++) {
      const readerId = ctx.readerIds[i];
      setSystemScanProgress({ current: i + 1, total: ctx.readerIds.length + 1, currentRoom: `Stopping ${readerId}...` });
      try {
        const res = await readerApi.stopContinuousScan(readerId);
        for (const tag of (res.tags || [])) rawTagEntries.push({ tag, readerId });
      } catch (err) {
        showToast(`Failed to stop scan on ${readerId}: ${err.message}`, 3000, true, "danger");
      }
    }

    setSystemScanProgress({ current: ctx.readerIds.length + 1, total: ctx.readerIds.length + 1, currentRoom: "Comparing with database..." });

    const { roomResults, allScannedTags } = await _processSystemScanResults(rawTagEntries, ctx);
    setSystemScanAllTags(allScannedTags);
    setSystemScanResults(roomResults);
    setSystemTagsPage(1);
    setSystemRoomsPage(1);
    setSystemScanProgress(null);
    setSystemScanning(false);
    setSystemScanTagCount(0);
    systemScanContextRef.current = null;
    _showCompareToast(roomResults, allScannedTags);
  };

  // ─── COMPUTED VALUES ────────────────────────────────────────

  const connectedCount = readers.filter((r) => r.connected).length;
  const disconnectedCount = readers.length - connectedCount;

  const readerOptions = readers.map((r) => ({
    label: `${r.readerId} (${r.ip})`,
    value: r.readerId,
  }));

  // For the scanner tab: only show assigned SCAN antennas (not THEFT)
  const scanAntennaOptions = scanReader
    ? antennas
        .filter((a) => a.readerId === scanReader && a.usage === "SCAN")
        .map((a) => ({
          label: `Antenna ${a.antennaNumber}${a.name ? ` — ${a.name}` : ""}`,
          value: String(a.antennaNumber),
        }))
    : [];

  const warehouseOptions = warehouses.map((w) => ({
    label: w.name,
    value: w._id || w.id || w.name,
  }));

  // Warehouse of the reader currently selected in the antenna modal.
  // Readers store this as `warehouseId`; `warehouseLocation` is the legacy
  // field name holding the same _id, which is why the GATE branch below
  // falls back to it too.
  const antennaFormWarehouseId = useMemo(() => {
    const reader = readers.find((r) => r.readerId === antennaForm.readerId);
    if (!reader) return "";
    return String(reader.warehouseId || reader.warehouseLocation || "");
  }, [readers, antennaForm.readerId]);

  // Partitions belong to exactly one warehouse, and the gate/scan logic
  // matches a tag's warehouse against the reader's. A SCAN antenna pointed
  // at a partition in some *other* warehouse therefore can never match
  // anything — it just silently reports an empty room forever. Listing every
  // partition in the system made that misconfiguration easy to walk into, so
  // scope the picker to the reader's own warehouse.
  const antennaRoomOptions = useMemo(() => {
    if (!antennaFormWarehouseId) return [];
    return rooms
      .filter(
        (r) => String(r.warehouseId?._id || r.warehouseId || "") === antennaFormWarehouseId
      )
      .map((r) => ({
        label: r.roomTitle || `Partition ${r.rid}`,
        value: String(r.rid),
      }));
  }, [rooms, antennaFormWarehouseId]);

  // Switching the reader can strip the chosen partition out of the list
  // above. Leaving the now-invalid rid selected would submit a partition
  // from the previous reader's warehouse, so drop it. Guarded on a non-empty
  // options list so this doesn't wipe the selection while rooms are still
  // loading, or while the modal is closed.
  useEffect(() => {
    if (!showAntennaModal) return;
    if (antennaForm.usage !== "SCAN") return;
    if (!antennaForm.roomId) return;
    if (antennaRoomOptions.length === 0) return;
    if (!antennaRoomOptions.some((o) => o.value === String(antennaForm.roomId))) {
      setAntennaForm((prev) => ({ ...prev, roomId: "" }));
    }
  }, [showAntennaModal, antennaForm.usage, antennaForm.roomId, antennaRoomOptions]);

  const SCANNER_PAGE_SIZE = 25;
  const SYSTEM_TAGS_PAGE_SIZE = 25;
  const SYSTEM_ROOMS_PAGE_SIZE = 8;
  const THEFT_EVENTS_PAGE_SIZE = 20;

  // Case-insensitive substring filter by Tag ID (TID). Empty query = show all.
  const _matchesTid = (tag, query) => {
    if (!query) return true;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (tag.tid || "").toLowerCase().includes(q);
  };

  const scannerTagsAll = scanResults?.tags || [];
  const scannerTags = scannerTagsAll.filter((t) => _matchesTid(t, scannerTidFilter));
  const scannerTotalPages = Math.max(1, Math.ceil(scannerTags.length / SCANNER_PAGE_SIZE));
  const scannerCurrentPage = Math.min(scannerPage, scannerTotalPages);
  const pagedScannerTags = scannerTags.slice(
    (scannerCurrentPage - 1) * SCANNER_PAGE_SIZE,
    scannerCurrentPage * SCANNER_PAGE_SIZE
  );

  const systemAllTagsRaw = systemScanAllTags || [];
  const systemAllTags = systemAllTagsRaw.filter((t) => _matchesTid(t, systemTidFilter));
  const systemTagsTotalPages = Math.max(1, Math.ceil(systemAllTags.length / SYSTEM_TAGS_PAGE_SIZE));
  const systemTagsCurrentPage = Math.min(systemTagsPage, systemTagsTotalPages);
  const pagedSystemAllTags = systemAllTags.slice(
    (systemTagsCurrentPage - 1) * SYSTEM_TAGS_PAGE_SIZE,
    systemTagsCurrentPage * SYSTEM_TAGS_PAGE_SIZE
  );

  const systemRooms = systemScanResults || [];
  const systemRoomsTotalPages = Math.max(1, Math.ceil(systemRooms.length / SYSTEM_ROOMS_PAGE_SIZE));
  const systemRoomsCurrentPage = Math.min(systemRoomsPage, systemRoomsTotalPages);
  const pagedSystemRooms = systemRooms.slice(
    (systemRoomsCurrentPage - 1) * SYSTEM_ROOMS_PAGE_SIZE,
    systemRoomsCurrentPage * SYSTEM_ROOMS_PAGE_SIZE
  );

  const theftTotalPages = Math.max(1, Math.ceil(theftEvents.length / THEFT_EVENTS_PAGE_SIZE));
  const theftCurrentPage = Math.min(theftEventsPage, theftTotalPages);
  const pagedTheftEvents = theftEvents.slice(
    (theftCurrentPage - 1) * THEFT_EVENTS_PAGE_SIZE,
    theftCurrentPage * THEFT_EVENTS_PAGE_SIZE
  );
  const allSelectedTheftOnPage =
    pagedTheftEvents.length > 0 &&
    pagedTheftEvents.filter((e) => e?._id).every((e) => selectedTheftEvents.has(e._id));

  const renderPager = (currentPage, totalPages, onPageChange) => {
    if (totalPages <= 1) return null;
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderTop: "1px solid var(--border-primary)",
          backgroundColor: "var(--bg-secondary)",
        }}
      >
        <button
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          style={{
            padding: "6px 12px",
            borderRadius: "8px",
            border: "1px solid var(--border-primary)",
            backgroundColor: currentPage === 1 ? "var(--bg-tertiary)" : "var(--bg-primary)",
            color: "var(--text-primary)",
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            opacity: currentPage === 1 ? 0.6 : 1,
          }}
        >
          <i className="fa fa-chevron-left" style={{ marginRight: 6 }} />
          Previous
        </button>
        <span style={{ fontSize: "13px", color: "var(--text-secondary)", fontWeight: 600 }}>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage >= totalPages}
          style={{
            padding: "6px 12px",
            borderRadius: "8px",
            border: "1px solid var(--border-primary)",
            backgroundColor: currentPage >= totalPages ? "var(--bg-tertiary)" : "var(--bg-primary)",
            color: "var(--text-primary)",
            cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
            opacity: currentPage >= totalPages ? 0.6 : 1,
          }}
        >
          Next
          <i className="fa fa-chevron-right" style={{ marginLeft: 6 }} />
        </button>
      </div>
    );
  };

  // ─── DISPLAY HELPERS (match Dashboard) ────────────────────

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString();
  };

  const getRSSIColor = (rssi) => {
    if (rssi >= -40) return "#4CAF50";
    if (rssi >= -60) return "#daae56";
    return "#f44336";
  };

  const getStateStyle = (state) => {
    switch (state) {
      case "READY":
        return { bg: "#DCFCE7", color: "#15803D", border: "#86EFAC", label: "Ready" };
      case "ON_DELIVERY":
        return { bg: "#FEF3C7", color: "#B45309", border: "#FCD34D", label: "On Delivery" };
      case "BLOCKED":
        return { bg: "#FEE2E2", color: "#DC2626", border: "#FCA5A5", label: "Blocked" };
      case "NOT_REGISTERED":
        return { bg: "#F3E8FF", color: "#7C3AED", border: "#C4B5FD", label: "Not Registered" };
      default:
        return { bg: "#F1F5F9", color: "#64748B", border: "#CBD5E1", label: state || "Unknown" };
    }
  };

  const getRoomName = (rid) => {
    if (rid === undefined || rid === null || rid === 0 || rid === "") return "Unassigned";
    const numRid = typeof rid === "string" ? parseInt(rid) : rid;
    const room = rooms.find((r) => r.rid === numRid);
    return room?.roomTitle || `Partition ${numRid}`;
  };

  // ─── TAB DEFINITIONS ───────────────────────────────────────

  const tabs = [
    { id: "readers", label: "Readers", icon: "fa-server" },
    { id: "desktopDevices", label: "Desktop POS Devices", icon: "fa-usb" },
    { id: "antennas", label: "Antennas", icon: "fa-wifi" },
    { id: "scanner", label: "Scanner", icon: "fa-search" },
    { id: "systemScan", label: "System Scan", icon: "fa-building" },
    { id: "theft", label: "Theft Detection", icon: "fa-shield" },
  ];

  // ─── RENDER ─────────────────────────────────────────────────

  return (
    <div className="operators-page">
      <div className="dashboard-content">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Operator Dashboard</h1>
            <p className="dashboard-subtitle">
              Manage RFID readers, antennas, and scanning
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="op-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`op-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`fa ${tab.icon}`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══════════════════ READERS TAB ═══════════════════ */}
        {activeTab === "readers" && (
          <>
            {/* Stats */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon" style={{ backgroundColor: "var(--info-subtle)" }}>
                  <i className="fa fa-server" style={{ color: "var(--info)" }} />
                </div>
                <div className="stat-content">
                  <h3 className="stat-value">{readers.length}</h3>
                  <p className="stat-label">Total Readers</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon" style={{ backgroundColor: "var(--success-subtle)" }}>
                  <i className="fa fa-check-circle" style={{ color: "var(--success)" }} />
                </div>
                <div className="stat-content">
                  <h3 className="stat-value">{connectedCount}</h3>
                  <p className="stat-label">Connected</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon" style={{ backgroundColor: "var(--error-subtle)" }}>
                  <i className="fa fa-times-circle" style={{ color: "var(--error)" }} />
                </div>
                <div className="stat-content">
                  <h3 className="stat-value">{disconnectedCount}</h3>
                  <p className="stat-label">Disconnected</p>
                </div>
              </div>
            </div>

            {/* Readers Table */}
            <div className="table-section">
              <div className="section-header">
                <h2 className="section-title">Readers</h2>
                <div className="dfcc gap-2">
                  <Button
                    value="Refresh"
                    iconLeft="fa-refresh"
                    onClick={() => fetchReaders()}
                    variant="outline"
                    size="sm"
                  />
                  <Button
                    value="Add Reader"
                    iconLeft="fa-plus"
                    onClick={() => {
                      setEditingReader(null);
                      setReaderForm({ readerId: "", ip: "", port: "", antennaCount: "", warehouseLocation: "" });
                      setShowReaderModal(true);
                    }}
                    variant="primary"
                    size="sm"
                  />
                </div>
              </div>

              <div className="table-container">
                {readersLoading ? (
                  <div className="loading-state">
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: "32px", color: "var(--text-tertiary)" }} />
                    <p>Loading readers...</p>
                  </div>
                ) : readers.length === 0 ? (
                  <div className="empty-state">
                    <i className="fa fa-server empty-state-icon" />
                    <p>No readers configured yet</p>
                  </div>
                ) : (
                  <table className="tags-table">
                    <thead>
                      <tr>
                        <th>Reader ID</th>
                        <th>IP Address</th>
                        <th>Port</th>
                        <th>Antennas</th>
                        <th>Warehouse</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {readers.map((r) => (
                        <tr key={r.readerId}>
                          <td>
                            <span className="tag-badge">{r.readerId}</span>
                          </td>
                          <td className="epc-cell">{r.ip}</td>
                          <td className="epc-cell">{r.port}</td>
                          <td>
                            <span className="antenna-badge">
                              <i className="fa fa-wifi" /> {r.antennaCount}
                            </span>
                          </td>
                          <td>
                            {(() => {
                              const whId = r.warehouseId || r.warehouseLocation;
                              const wh = whId
                                ? warehouses.find((w) => String(w._id) === String(whId))
                                : null;
                              return wh
                                ? <span className="tag-badge" style={{ fontWeight: 600 }}><i className="fa fa-building" style={{ marginRight: 5, opacity: 0.6 }} />{wh.name}</span>
                                : <span style={{ color: "var(--text-tertiary)" }}>—</span>;
                            })()}
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span className={`state-badge ${r.connected ? "ready" : "blocked"}`}>
                                {r.connected ? "Connected" : "Disconnected"}
                              </span>
                              {r.alarming && (
                                <span
                                  className="state-badge blocked"
                                  style={{ backgroundColor: "#DC2626", color: "#fff", border: "1px solid #7F1D1D", animation: "pulse 1.2s ease-in-out infinite" }}
                                  title="Theft alarm latched — click Stop Alarm to clear"
                                >
                                  <i className="fa fa-bell" style={{ marginRight: 4 }} /> ALARM
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ minWidth: r.alarming ? 260 : undefined, whiteSpace: "nowrap" }}>
                            <div className="action-buttons" style={{ flexWrap: "wrap", rowGap: 4 }}>
                              {r.alarming && (
                                <button
                                  className="action-btn delete-btn"
                                  onClick={() => handleStopAlarm(r.readerId)}
                                  disabled={stoppingAlarm === r.readerId}
                                  title="Stop Alarm"
                                  style={{
                                    backgroundColor: "#DC2626",
                                    color: "#fff",
                                    border: "1px solid #7F1D1D",
                                    width: "auto",
                                    height: 32,
                                    padding: "0 12px",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                    gap: 6,
                                  }}
                                >
                                  {stoppingAlarm === r.readerId ? (
                                    <i className="fa fa-spinner fa-spin" />
                                  ) : (
                                    <><i className="fa fa-bell-slash" /> Stop Alarm</>
                                  )}
                                </button>
                              )}
                              {r.connected ? (
                                <button
                                  className="action-btn delete-btn"
                                  onClick={() => handleDisconnect(r.readerId)}
                                  disabled={connectingReader === r.readerId}
                                  title="Disconnect"
                                >
                                  {connectingReader === r.readerId ? (
                                    <i className="fa fa-spinner fa-spin" />
                                  ) : (
                                    <i className="fa fa-unlink" />
                                  )}
                                </button>
                              ) : (
                                <button
                                  className="action-btn edit-btn"
                                  onClick={() => handleConnect(r.readerId)}
                                  disabled={connectingReader === r.readerId}
                                  title="Connect"
                                >
                                  {connectingReader === r.readerId ? (
                                    <i className="fa fa-spinner fa-spin" />
                                  ) : (
                                    <i className="fa fa-link" />
                                  )}
                                </button>
                              )}
                              {r.connected && (
                                <button
                                  className="action-btn"
                                  onClick={() => handleTestGpo(r.readerId)}
                                  disabled={gpoTesting === r.readerId}
                                  title={`${getReaderGpoPins(r.readerId).map((p) => `GPO${p}`).join(" + ")} ${
                                    gpoTestOn[r.readerId]
                                      ? "is ON — click to turn OFF"
                                      : "is OFF — click to turn ON (test wiring)"
                                  }`}
                                  style={{
                                    backgroundColor: gpoTestOn[r.readerId] ? "#DC2626" : "#F59E0B",
                                    color: "#fff",
                                    border: gpoTestOn[r.readerId]
                                      ? "1px solid #7F1D1D"
                                      : "1px solid #B45309",
                                  }}
                                >
                                  {gpoTesting === r.readerId ? (
                                    <i className="fa fa-spinner fa-spin" />
                                  ) : (
                                    <i className={gpoTestOn[r.readerId] ? "fa fa-bolt" : "fa fa-bolt"} />
                                  )}
                                </button>
                              )}
                              <button
                                className="action-btn edit-btn"
                                onClick={() => {
                                  setEditingReader(r);
                                  setReaderForm({
                                    readerId: r.readerId,
                                    ip: r.ip,
                                    port: String(r.port),
                                    antennaCount: String(r.antennaCount),
                                    warehouseLocation: r.warehouseLocation || "",
                                  });
                                  setShowReaderModal(true);
                                }}
                                title="Edit Reader"
                              >
                                <i className="fa fa-edit" />
                              </button>
                              <button
                                className="action-btn delete-btn"
                                onClick={() => handleDeleteReader(r.readerId)}
                                title="Delete Reader"
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        {/* ═══════════════ DESKTOP POS DEVICES TAB ═══════════════ */}
        {activeTab === "desktopDevices" && (
          <>
            <div className="table-section">
              <div className="section-header">
                <h2 className="section-title">Configured Desktop POS Devices</h2>
                <div className="dfcc gap-2">
                  <Button
                    value="Refresh"
                    iconLeft="fa-refresh"
                    variant="outline"
                    size="sm"
                    onClick={fetchDesktopDevices}
                  />
                  <Button
                    value="Add Desktop Device"
                    iconLeft="fa-plus"
                    variant="primary"
                    size="sm"
                    onClick={handleOpenDesktopCreate}
                  />
                </div>
              </div>
              <div className="table-container">
                {desktopLoading ? (
                  <div className="loading-state">
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: "32px", color: "var(--text-tertiary)" }} />
                    <p>Loading desktop devices...</p>
                  </div>
                ) : desktopDevices.length === 0 ? (
                  <div className="empty-state">
                    <i className="fa fa-usb empty-state-icon" />
                    <p>No desktop POS devices configured</p>
                  </div>
                ) : (
                  <table className="tags-table">
                    <thead>
                      <tr>
                        <th>Device ID</th>
                        <th>Name</th>
                        <th>Connection</th>
                        <th>Path / Port</th>
                        <th>Power</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {desktopDevices.map((d) => (
                        <tr key={d.deviceId}>
                          <td><span className="tag-badge">{d.deviceId}</span></td>
                          <td>{d.name}</td>
                          <td>{d.connectionType === "SERIAL" ? "USB→RS232" : "USB HID"}</td>
                          <td className="epc-cell" style={{ maxWidth: 420 }}>
                            {d.connectionType === "SERIAL" ? `${d.serialPort || "—"} @ ${d.baudRate || 115200}` : d.usbPath}
                          </td>
                          <td><span className="tag-badge">{Number(d.power || 30)} dBm</span></td>
                          <td>
                            <span className="status-badge" style={{ backgroundColor: d.running ? "#DCFCE7" : "#F1F5F9", color: d.running ? "#15803D" : "#64748B" }}>
                              {d.running ? "RUNNING" : "STOPPED"}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button className="action-btn edit-btn" onClick={() => handleEditDesktopDevice(d)} title="Edit Device">
                                <i className="fa fa-edit" />
                              </button>
                              <button className="action-btn delete-btn" onClick={() => handleDeleteDesktopDevice(d.deviceId)} title="Delete Device">
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        {/* ═══════════════════ ANTENNAS TAB ═══════════════════ */}
        {activeTab === "antennas" && (
          <>
            {/* Filters */}
            <div className="filters-section">
              <h2 className="section-title">Filter Antennas</h2>
              <div className="filters-grid">
                <Dropdown
                  options={[
                    { label: "All Readers", value: "all" },
                    ...readers.map((r) => ({ label: r.readerId, value: r.readerId })),
                  ]}
                  value={antennaFilterReader}
                  onChange={setAntennaFilterReader}
                  placeholder="Filter by Reader"
                />
              </div>
            </div>

            {/* Antennas Table */}
            <div className="table-section">
              <div className="section-header">
                <h2 className="section-title">Antennas</h2>
                <div className="dfcc gap-2">
                  <Button
                    value="Refresh"
                    iconLeft="fa-refresh"
                    onClick={() => fetchAntennas()}
                    variant="outline"
                    size="sm"
                  />
                  <Button
                    value="Assign Antenna"
                    iconLeft="fa-plus"
                    onClick={() => {
                      setEditingAntenna(null);
                      setAntennaForm({ readerId: "", antennaNumber: "", usage: "", name: "", roomId: "", warehouseId: "", power: "30", gpoPin: "2" });
                      setShowAntennaModal(true);
                    }}
                    variant="primary"
                    size="sm"
                  />
                </div>
              </div>

              <div className="table-container">
                {antennasLoading ? (
                  <div className="loading-state">
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: "32px", color: "var(--text-tertiary)" }} />
                    <p>Loading antennas...</p>
                  </div>
                ) : antennas.length === 0 ? (
                  <div className="empty-state">
                    <i className="fa fa-wifi empty-state-icon" />
                    <p>No antennas assigned yet</p>
                  </div>
                ) : (
                  <table className="tags-table">
                    <thead>
                      <tr>
                        <th>Reader ID</th>
                        <th>Antenna #</th>
                        <th>Usage</th>
                        <th>Name</th>
                        <th>Partition</th>
                        <th>Alarm GPO</th>
                        <th>Power</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {antennas.map((a, idx) => (
                        <tr key={`${a.readerId}-${a.antennaNumber}-${idx}`}>
                          <td>
                            <span className="tag-badge">{a.readerId}</span>
                          </td>
                          <td>
                            <span className="antenna-badge">
                              <i className="fa fa-wifi" /> {a.antennaNumber}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`state-badge ${
                                (a.usage === "GATE" || a.usage === "THEFT" || a.usage === "CHECKING") ? "blocked" : "ready"
                              }`}
                            >
                              {a.usage === "THEFT" || a.usage === "CHECKING" ? "GATE" : a.usage}
                            </span>
                          </td>
                          <td>{a.name || "—"}</td>
                          <td>
                            <span className="room-badge">{getRoomName(a.roomId)}</span>
                          </td>
                          <td data-label="Alarm GPO">
                            {(a.usage === "GATE" || a.usage === "THEFT" || a.usage === "CHECKING") ? (
                              // Antennas assigned before gpoPin existed have no
                              // field; the backend treats that as GPO2, so show
                              // the same thing rather than an empty cell.
                              <span className="antenna-badge" title="Relay this gate closes when it alarms">
                                <i className="fa fa-bolt" /> GPO{a.gpoPin || 2}
                              </span>
                            ) : (
                              <span style={{ color: "var(--text-tertiary)" }}>—</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: "120px" }}>
                              {(() => {
                                const antKey = `${a.readerId}_${a.antennaNumber}`;
                                const displayVal = localAntPower[antKey] ?? a.power ?? 30;
                                return (
                                  <>
                                    <input
                                      type="range"
                                      min="0"
                                      max="33"
                                      step="1"
                                      value={displayVal}
                                      style={{ width: "70px", accentColor: "var(--primary)" }}
                                      onChange={(e) => {
                                        setLocalAntPower((prev) => ({
                                          ...prev,
                                          [antKey]: parseInt(e.target.value),
                                        }));
                                      }}
                                      onMouseUp={async (e) => {
                                        const val = parseInt(e.target.value);
                                        try {
                                          await readerApi.setAntennaPower(a.readerId, a.antennaNumber, val);
                                        } catch {}
                                        fetchAntennas();
                                      }}
                                      onTouchEnd={async (e) => {
                                        const val = parseInt(e.target.value);
                                        try {
                                          await readerApi.setAntennaPower(a.readerId, a.antennaNumber, val);
                                        } catch {}
                                        fetchAntennas();
                                      }}
                                    />
                                    <span style={{ fontSize: "12px", fontWeight: 600, minWidth: "36px" }}>
                                      {displayVal} dBm
                                    </span>
                                  </>
                                );
                              })()}
                            </div>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="action-btn edit-btn"
                                onClick={() => {
                                  setEditingAntenna(a);
                                  setAntennaForm({
                                    readerId: a.readerId,
                                    antennaNumber: String(a.antennaNumber),
                                    usage: a.usage || "",
                                    name: a.name || "",
                                    roomId: a.roomId != null ? String(a.roomId) : "",
                                    warehouseId: a.warehouseId ? String(a.warehouseId) : "",
                                    power: String(a.power ?? 30),
                                    gpoPin: String(a.gpoPin ?? 2),
                                  });
                                  setShowAntennaModal(true);
                                }}
                                title="Edit Antenna"
                              >
                                <i className="fa fa-edit" />
                              </button>
                              <button
                                className="action-btn delete-btn"
                                onClick={() => handleDeleteAntenna(a.readerId, a.antennaNumber)}
                                title="Delete Antenna"
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        {/* ═══════════════════ SCANNER TAB ═══════════════════ */}
        {activeTab === "scanner" && (
          <>
            {/* Scanner Controls */}
            <div className="filters-section">
              <h2 className="section-title">Scan Configuration</h2>
              <div className="filters-grid">
                <Dropdown
                  options={readerOptions}
                  value={scanReader}
                  onChange={(val) => {
                    setScanReader(val);
                    setScanAntennas([]);
                    setScanPowerOverrides({});
                  }}
                  placeholder="Select Reader"
                  disabled={scanning}
                />
                <Dropdown
                  options={scanAntennaOptions}
                  value={scanAntennas}
                  onChange={setScanAntennas}
                  placeholder={scanAntennaOptions.length === 0 && scanReader ? "No scan antennas" : "Select Antennas"}
                  disabled={!scanReader || scanAntennaOptions.length === 0 || scanning}
                  multi
                />
                <Dropdown
                  options={[
                    { label: "1 second", value: "1" },
                    { label: "2 seconds", value: "2" },
                    { label: "3 seconds", value: "3" },
                    { label: "5 seconds", value: "5" },
                    { label: "10 seconds", value: "10" },
                    { label: "Until Stopped", value: "continuous" },
                  ]}
                  value={continuousMode ? "continuous" : scanDuration}
                  onChange={(val) => {
                    if (val === "continuous") {
                      setContinuousMode(true);
                    } else {
                      setContinuousMode(false);
                      setScanDuration(val);
                    }
                  }}
                  placeholder="Scan Duration"
                  disabled={scanning}
                />
                <div className="dfcc" style={{ alignItems: "flex-end", gap: "8px", flexDirection: "row" }}>
                  {scanning && continuousMode ? (
                    <Button
                      value="Stop Scan"
                      iconLeft="fa-stop"
                      onClick={handleStopScan}
                      variant="danger"
                    />
                  ) : (
                    <Button
                      value={scanning ? "Scanning..." : "Start Scan"}
                      iconLeft={scanning ? "fa-spinner fa-spin" : "fa-search"}
                      onClick={handleScan}
                      variant="primary"
                      disabled={scanning || !scanReader || scanAntennas.length === 0}
                    />
                  )}
                </div>
              </div>

              {/* Power overrides for selected antennas */}
              {scanReader && scanAntennas.length > 0 && !scanning && (
                <div style={{ marginTop: "12px", padding: "12px 16px", borderRadius: "10px", border: "1px solid #E2E8F0", backgroundColor: "var(--bg-secondary)" }}>
                  <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <i className="fa fa-signal" style={{ color: "var(--primary)" }} />
                    Scan Power (per antenna)
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "14px" }}>
                    {scanAntennas.map((antNum) => {
                      const antDoc = antennas.find((a) => a.readerId === scanReader && a.antennaNumber === parseInt(antNum));
                      const preferred = antDoc?.power ?? 30;
                      const current = scanPowerOverrides[antNum] ?? preferred;
                      return (
                        <div key={antNum} style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: "200px" }}>
                          <span style={{ fontSize: "12px", fontWeight: 600, minWidth: "45px" }}>Ant {antNum}</span>
                          <input
                            type="range"
                            min="0"
                            max="33"
                            step="1"
                            value={current}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              setScanPowerOverrides((prev) => ({ ...prev, [antNum]: val }));
                            }}
                            style={{ flex: 1, accentColor: "var(--primary)" }}
                          />
                          <span style={{ fontSize: "12px", fontWeight: 600, minWidth: "42px" }}>{current} dBm</span>
                          {scanPowerOverrides[antNum] !== undefined && scanPowerOverrides[antNum] !== preferred && (
                            <button
                              onClick={() => {
                                setScanPowerOverrides((prev) => {
                                  const next = { ...prev };
                                  delete next[antNum];
                                  return next;
                                });
                              }}
                              title="Reset to preferred"
                              style={{ padding: "2px 6px", fontSize: "11px", border: "1px solid #CBD5E1", borderRadius: "4px", cursor: "pointer", backgroundColor: "transparent", color: "var(--text-tertiary)" }}
                            >
                              <i className="fa fa-undo" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Scan Results */}
            <div className="table-section">
              <div className="section-header">
                <h2 className="section-title">Scan Results</h2>
                <div className="dfcc gap-2">
                  {scanResults && (
                    <span className="results-count">
                      {scanResults.continuous && scanning ? (
                        <><i className="fa fa-circle" style={{ color: "#ef4444", fontSize: "10px", marginRight: "6px", animation: "pulse 1s infinite" }} />{scanResults.tagsFound} tag(s) found — scanning...</>
                      ) : (
                        <>{scanResults.tagsFound} tag(s) found{scanResults.duration ? ` in ${scanResults.duration}s` : ""}</>
                      )}
                    </span>
                  )}
                </div>
              </div>

              <div className="table-container">
                {scanning ? (
                  <div className="loading-state">
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: "32px", color: "var(--text-tertiary)" }} />
                    <p>Scanning... Please wait</p>
                  </div>
                ) : !scanResults ? (
                  <div className="empty-state">
                    <i className="fa fa-search empty-state-icon" />
                    <p>Configure a scan and press Start</p>
                  </div>
                ) : scanResults.tagsFound === 0 ? (
                  <div className="empty-state">
                    <i className="fa fa-inbox empty-state-icon" />
                    <p>No tags detected during scan</p>
                  </div>
                ) : (
                  <>
                  <div className="tag-filter-bar" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid var(--border-subtle, #e5e7eb)" }}>
                    <i className="fa fa-filter" style={{ color: "var(--text-tertiary)" }} />
                    <input
                      type="text"
                      value={scannerTidFilter}
                      onChange={(e) => setScannerTidFilter(e.target.value)}
                      placeholder="Filter by Tag ID..."
                      style={{ flex: 1, padding: "6px 10px", border: "1px solid var(--border-subtle, #e5e7eb)", borderRadius: 6, fontSize: 14 }}
                    />
                    {scannerTidFilter && (
                      <button
                        type="button"
                        onClick={() => setScannerTidFilter("")}
                        style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--text-tertiary)" }}
                        title="Clear filter"
                      >
                        <i className="fa fa-times" />
                      </button>
                    )}
                    <span style={{ color: "var(--text-tertiary)", fontSize: 12, whiteSpace: "nowrap" }}>
                      {scannerTags.length} of {scannerTagsAll.length}
                    </span>
                  </div>
                  {scannerTags.length === 0 ? (
                    <div className="empty-state">
                      <i className="fa fa-filter empty-state-icon" />
                      <p>No tags match "{scannerTidFilter}"</p>
                    </div>
                  ) : (
                  <table className="tags-table">
                    <thead>
                      <tr>
                        <th>Tag ID</th>
                        <th>Name</th>
                        <th>EPC</th>
                        <th>State</th>
                        <th>Antenna</th>
                        <th>Partition</th>
                        <th>RSSI</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedScannerTags.map((t, idx) => {
                        const isRegistered = t.registered !== false;
                        return (
                        <tr key={`${t.epc}-${(scannerCurrentPage - 1) * SCANNER_PAGE_SIZE + idx}`} style={!isRegistered ? { opacity: 0.85 } : {}}>
                          <td data-label="Tag ID">
                            <span
                              className="tag-badge"
                              onClick={() => openTagDetails(t.tid)}
                              role="button"
                              tabIndex={t.tid ? 0 : -1}
                              onKeyDown={(e) => { if (e.key === "Enter") openTagDetails(t.tid); }}
                              title={t.tid ? "Open tag details" : undefined}
                              style={{ cursor: t.tid ? "pointer" : "default", textDecoration: t.tid ? "underline dotted" : "none", textUnderlineOffset: 3 }}
                            >{t.tid || "—"}</span>
                          </td>
                          <td data-label="Name">
                            {isRegistered ? (
                              <span className="epc-cell fw-bold fs-6">{t.name || "UNNAMED"}</span>
                            ) : (
                              <span style={{ color: "#7C3AED", fontStyle: "italic", fontWeight: 500 }}>
                                <i className="fa fa-exclamation-triangle" style={{ marginRight: 5 }} />
                                Not in Database
                              </span>
                            )}
                          </td>
                          <td data-label="EPC" className="epc-cell">{t.epc}</td>
                          <td data-label="State">
                            <span
                              className="state-badge"
                              style={{
                                backgroundColor: getStateStyle(t.state).bg,
                                color: getStateStyle(t.state).color,
                                border: `1px solid ${getStateStyle(t.state).border}`
                              }}
                            >
                              {getStateStyle(t.state).label}
                            </span>
                          </td>
                          <td data-label="Antenna">
                            <span className="antenna-badge">
                              <i className="fa fa-wifi" /> Ant {t.antenna}
                            </span>
                          </td>
                          <td data-label="Partition">
                            <span className="room-badge">{t.roomName || getRoomName(t.rid)}</span>
                          </td>
                          <td data-label="RSSI">
                            <span
                              className="rssi-badge"
                              style={{
                                backgroundColor: getRSSIColor(t.rssi) + "20",
                                color: getRSSIColor(t.rssi),
                                border: `1px solid ${getRSSIColor(t.rssi)}`
                              }}
                            >
                              {t.rssi} dBm
                            </span>
                          </td>
                          <td data-label="Timestamp" className="timestamp-cell">
                            {formatDate(t.scanTimestamp || t.timestamp)}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  )}
                  </>
                )}
                {!scanning && !!scanResults && scanResults.tagsFound > 0 && scannerTags.length > 0 && renderPager(scannerCurrentPage, scannerTotalPages, setScannerPage)}
              </div>
            </div>
          </>
        )}

        {/* ═══════════════════ SYSTEM SCAN TAB ═══════════════════ */}
        {activeTab === "systemScan" && (
          <>
            {/* Description + Controls */}
            <div className="filters-section">
              <h2 className="section-title">Full System Scan</h2>
              <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "12px", lineHeight: "1.5" }}>
                Scan every SCAN antenna on all connected readers, then compare the results against
                the database for each room. Identifies <strong>matched</strong>, <strong>missing</strong>, and <strong>extra</strong> tags per room.
              </p>
              <div className="filters-grid">
                <Dropdown
                  options={[
                    { label: "1 second", value: "1" },
                    { label: "2 seconds", value: "2" },
                    { label: "3 seconds", value: "3" },
                    { label: "5 seconds", value: "5" },
                    { label: "10 seconds", value: "10" },
                    { label: "Until Stopped", value: "continuous" },
                  ]}
                  value={systemContinuousMode ? "continuous" : systemScanDuration}
                  onChange={(val) => {
                    if (val === "continuous") {
                      setSystemContinuousMode(true);
                    } else {
                      setSystemContinuousMode(false);
                      setSystemScanDuration(val);
                    }
                  }}
                  placeholder="Scan Duration (per reader)"
                  disabled={systemScanning}
                />
                <div className="dfcc" style={{ alignItems: "flex-end", flexDirection: "row", gap: "8px" }}>
                  {systemScanning && systemContinuousMode ? (
                    <Button
                      value="Stop Scan"
                      iconLeft="fa-stop"
                      onClick={handleStopSystemScan}
                      variant="danger"
                    />
                  ) : (
                    <Button
                      value={systemScanning ? "Scanning..." : "Scan System"}
                      iconLeft={systemScanning ? "fa-spinner fa-spin" : "fa-building"}
                      onClick={handleSystemScan}
                      variant="primary"
                      disabled={systemScanning}
                    />
                  )}
                </div>
              </div>

              {/* Progress indicator */}
              {systemScanProgress && (
                <div className="system-scan-progress">
                  <div className="system-scan-progress-bar">
                    <div
                      className="system-scan-progress-fill"
                      style={{
                        width: `${(systemScanProgress.current / systemScanProgress.total) * 100}%`,
                      }}
                    />
                  </div>
                  <div className="system-scan-progress-text">
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: "6px" }} />
                    Step {systemScanProgress.current} of {systemScanProgress.total} — {systemScanProgress.currentRoom}
                  </div>
                </div>
              )}

              {/* Live tag count for continuous mode */}
              {systemScanning && systemContinuousMode && !systemScanProgress && (
                <div className="system-scan-progress" style={{ marginTop: "14px" }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: "10px",
                    padding: "12px 16px", borderRadius: "10px",
                    border: "1px solid #86EFAC", backgroundColor: "#F0FDF4",
                  }}>
                    <i className="fa fa-circle" style={{ color: "#ef4444", fontSize: "10px", animation: "pulse 1s infinite" }} />
                    <span style={{ fontWeight: 700, fontSize: "14px", color: "#15803D" }}>
                      Scanning all rooms...
                    </span>
                    <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                      {systemScanTagCount} tag(s) found so far
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Results */}
            {systemScanResults && (
              <>
                {/* Summary cards */}
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: "#E0E7FF" }}>
                      <i className="fa fa-tags" style={{ color: "#4F46E5" }} />
                    </div>
                    <div className="stat-content">
                      <h3 className="stat-value">{systemScanAllTags.length}</h3>
                      <p className="stat-label">Tags Scanned</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: "var(--info-subtle)" }}>
                      <i className="fa fa-building" style={{ color: "var(--info)" }} />
                    </div>
                    <div className="stat-content">
                      <h3 className="stat-value">{systemScanResults.length}</h3>
                      <p className="stat-label">Partitions Scanned</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: "var(--success-subtle)" }}>
                      <i className="fa fa-check" style={{ color: "var(--success)" }} />
                    </div>
                    <div className="stat-content">
                      <h3 className="stat-value">
                        {systemScanResults.reduce((s, r) => s + (r.summary?.alreadyExistsCount || 0), 0)}
                      </h3>
                      <p className="stat-label">Matched</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: "var(--error-subtle)" }}>
                      <i className="fa fa-exclamation-circle" style={{ color: "var(--error)" }} />
                    </div>
                    <div className="stat-content">
                      <h3 className="stat-value">
                        {systemScanResults.reduce((s, r) => s + (r.summary?.missingCount || 0), 0)}
                      </h3>
                      <p className="stat-label">Missing</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: "#FEF3C7" }}>
                      <i className="fa fa-exchange" style={{ color: "#B45309" }} />
                    </div>
                    <div className="stat-content">
                      <h3 className="stat-value">
                        {systemScanResults.reduce((s, r) => s + (r.summary?.misplacedCount || 0), 0)}
                      </h3>
                      <p className="stat-label">Misplaced</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: "#FEF3C7" }}>
                      <i className="fa fa-plus-circle" style={{ color: "#B45309" }} />
                    </div>
                    <div className="stat-content">
                      <h3 className="stat-value">
                        {systemScanResults.reduce((s, r) => s + (r.summary?.extraCount || 0), 0)}
                      </h3>
                      <p className="stat-label">Extra</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: "#F3E8FF" }}>
                      <i className="fa fa-question-circle" style={{ color: "#7C3AED" }} />
                    </div>
                    <div className="stat-content">
                      <h3 className="stat-value">
                        {systemScanAllTags.filter((t) => t.registered === false).length}
                      </h3>
                      <p className="stat-label">Not Registered</p>
                    </div>
                  </div>
                </div>

                {/* All Scanned Tags table */}
                {systemScanAllTags.length > 0 && (
                  <div className="table-section">
                    <div className="section-header">
                      <h2 className="section-title">
                        All Scanned Tags
                        <span style={{ fontSize: "13px", fontWeight: 400, color: "var(--text-tertiary)", marginLeft: "8px" }}>
                          ({systemScanAllTags.length})
                        </span>
                      </h2>
                    </div>
                    <div className="table-container">
                      <div className="tag-filter-bar" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid var(--border-subtle, #e5e7eb)" }}>
                        <i className="fa fa-filter" style={{ color: "var(--text-tertiary)" }} />
                        <input
                          type="text"
                          value={systemTidFilter}
                          onChange={(e) => setSystemTidFilter(e.target.value)}
                          placeholder="Filter by Tag ID..."
                          style={{ flex: 1, padding: "6px 10px", border: "1px solid var(--border-subtle, #e5e7eb)", borderRadius: 6, fontSize: 14 }}
                        />
                        {systemTidFilter && (
                          <button
                            type="button"
                            onClick={() => setSystemTidFilter("")}
                            style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--text-tertiary)" }}
                            title="Clear filter"
                          >
                            <i className="fa fa-times" />
                          </button>
                        )}
                        <span style={{ color: "var(--text-tertiary)", fontSize: 12, whiteSpace: "nowrap" }}>
                          {systemAllTags.length} of {systemAllTagsRaw.length}
                        </span>
                      </div>
                      {systemAllTags.length === 0 ? (
                        <div className="empty-state">
                          <i className="fa fa-filter empty-state-icon" />
                          <p>No tags match "{systemTidFilter}"</p>
                        </div>
                      ) : (
                      <table className="tags-table">
                        <thead>
                          <tr>
                            <th>Tag ID</th>
                            <th>Name</th>
                            <th>EPC</th>
                            <th>State</th>
                            <th>Antenna</th>
                            <th>Partition</th>
                            <th>RSSI</th>
                            <th>Timestamp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedSystemAllTags.map((t, idx) => {
                            const isRegistered = t.registered !== false;
                            return (
                              <tr key={`${t.epc}-${(systemTagsCurrentPage - 1) * SYSTEM_TAGS_PAGE_SIZE + idx}`} style={!isRegistered ? { opacity: 0.85 } : {}}>
                                <td data-label="Tag ID">
                                  <span
                              className="tag-badge"
                              onClick={() => openTagDetails(t.tid)}
                              role="button"
                              tabIndex={t.tid ? 0 : -1}
                              onKeyDown={(e) => { if (e.key === "Enter") openTagDetails(t.tid); }}
                              title={t.tid ? "Open tag details" : undefined}
                              style={{ cursor: t.tid ? "pointer" : "default", textDecoration: t.tid ? "underline dotted" : "none", textUnderlineOffset: 3 }}
                            >{t.tid || "—"}</span>
                                </td>
                                <td data-label="Name">
                                  {isRegistered ? (
                                    <span className="epc-cell fw-bold fs-6">{t.name || "UNNAMED"}</span>
                                  ) : (
                                    <span style={{ color: "#7C3AED", fontStyle: "italic", fontWeight: 500 }}>
                                      <i className="fa fa-exclamation-triangle" style={{ marginRight: 5 }} />
                                      Not in Database
                                    </span>
                                  )}
                                </td>
                                <td data-label="EPC" className="epc-cell">{t.epc}</td>
                                <td data-label="State">
                                  <span
                                    className="state-badge"
                                    style={{
                                      backgroundColor: getStateStyle(t.state).bg,
                                      color: getStateStyle(t.state).color,
                                      border: `1px solid ${getStateStyle(t.state).border}`
                                    }}
                                  >
                                    {getStateStyle(t.state).label}
                                  </span>
                                </td>
                                <td data-label="Antenna">
                                  <span className="antenna-badge">
                                    <i className="fa fa-wifi" /> Ant {t.antenna}
                                  </span>
                                </td>
                                <td data-label="Partition">
                                  <span className="room-badge">{t.roomName || getRoomName(t.rid)}</span>
                                </td>
                                <td data-label="RSSI">
                                  <span
                                    className="rssi-badge"
                                    style={{
                                      backgroundColor: getRSSIColor(t.rssi) + "20",
                                      color: getRSSIColor(t.rssi),
                                      border: `1px solid ${getRSSIColor(t.rssi)}`
                                    }}
                                  >
                                    {t.rssi} dBm
                                  </span>
                                </td>
                                <td data-label="Timestamp" className="timestamp-cell">
                                  {formatDate(t.scanTimestamp || t.timestamp)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      )}
                    </div>
                    {systemAllTags.length > 0 && renderPager(systemTagsCurrentPage, systemTagsTotalPages, setSystemTagsPage)}
                  </div>
                )}

                {/* Per-room results */}
                <div className="table-section">
                  <div className="section-header">
                    <h2 className="section-title">Results by Partition</h2>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "16px" }}>
                    {pagedSystemRooms.map((room) => {
                      const isExpanded = systemScanExpandedRoom === room.roomId;
                      const hasMissing = (room.summary?.missingCount || 0) > 0;
                      const hasMisplaced = (room.summary?.misplacedCount || 0) > 0;
                      const hasExtra = (room.summary?.extraCount || 0) > 0;
                      const hasIssues = hasMissing || hasMisplaced || hasExtra;
                      const hasError = !!room.error;

                      return (
                        <div key={room.roomId} className="system-scan-room-card">
                          {/* Room header (clickable) */}
                          <div
                            className={`system-scan-room-header ${hasError ? "error" : hasIssues ? "warning" : "ok"}`}
                            onClick={() => setSystemScanExpandedRoom(isExpanded ? null : room.roomId)}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                              <i
                                className={`fa ${hasError ? "fa-times-circle" : hasIssues ? "fa-exclamation-triangle" : "fa-check-circle"}`}
                                style={{
                                  fontSize: "20px",
                                  color: hasError ? "#DC2626" : hasIssues ? "#B45309" : "#15803D",
                                }}
                              />
                              <div>
                                <div style={{ fontWeight: 700, fontSize: "14px" }}>{room.roomName}</div>
                                <div style={{ fontSize: "12px", color: "var(--text-tertiary)" }}>
                                  {hasError ? (
                                    <span style={{ color: "#DC2626" }}>Error: {room.error}</span>
                                  ) : (
                                    <>
                                      {room.scannedCount} scanned &middot;{" "}
                                      {room.summary?.totalExisting || 0} in DB &middot;{" "}
                                      <span style={{ color: "#15803D", fontWeight: 600 }}>
                                        {room.summary?.alreadyExistsCount || 0} matched
                                      </span>
                                      {(room.summary?.missingCount || 0) > 0 && (
                                        <>
                                          {" "}&middot;{" "}
                                          <span style={{ color: "#DC2626", fontWeight: 600 }}>
                                            {room.summary.missingCount} missing
                                          </span>
                                        </>
                                      )}
                                      {(room.summary?.misplacedCount || 0) > 0 && (
                                        <>
                                          {" "}&middot;{" "}
                                          <span style={{ color: "#B45309", fontWeight: 600 }}>
                                            {room.summary.misplacedCount} misplaced
                                          </span>
                                        </>
                                      )}
                                      {(room.summary?.extraCount || 0) > 0 && (
                                        <>
                                          {" "}&middot;{" "}
                                          <span style={{ color: "#B45309", fontWeight: 600 }}>
                                            {room.summary.extraCount} extra
                                          </span>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <i className={`fa fa-chevron-${isExpanded ? "up" : "down"}`} style={{ color: "var(--text-tertiary)" }} />
                          </div>

                          {/* Expanded detail */}
                          {isExpanded && !hasError && (
                            <div className="system-scan-room-detail">
                              {/* Matched */}
                              {room.alreadyExists?.tags?.length > 0 && (
                                <div className="system-scan-section">
                                  <div className="system-scan-section-title ok">
                                    <i className="fa fa-check" /> Matched ({room.alreadyExists.tags.length})
                                  </div>
                                  <table className="tags-table compact">
                                    <thead>
                                      <tr>
                                        <th>TID</th>
                                        <th>Name</th>
                                        <th>EPC</th>
                                        <th>State</th>
                                        <th>Antenna</th>
                                        <th>RSSI (scan)</th>
                                        <th>Timestamp</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {room.alreadyExists.tags.map((t, i) => (
                                        <tr key={`m-${i}`}>
                                          <td data-label="TID">
                                            <span
                              className="tag-badge"
                              onClick={() => openTagDetails(t.tid)}
                              role="button"
                              tabIndex={t.tid ? 0 : -1}
                              onKeyDown={(e) => { if (e.key === "Enter") openTagDetails(t.tid); }}
                              title={t.tid ? "Open tag details" : undefined}
                              style={{ cursor: t.tid ? "pointer" : "default", textDecoration: t.tid ? "underline dotted" : "none", textUnderlineOffset: 3 }}
                            >{t.tid || "—"}</span>
                                          </td>
                                          <td data-label="Name">
                                            <span className="epc-cell fw-bold fs-6">{t.name || "UNNAMED"}</span>
                                          </td>
                                          <td data-label="EPC" className="epc-cell" style={{ fontSize: "12px" }}>{t.epc}</td>
                                          <td data-label="State">
                                            <span className="state-badge" style={{
                                              backgroundColor: getStateStyle(t.state).bg,
                                              color: getStateStyle(t.state).color,
                                              border: `1px solid ${getStateStyle(t.state).border}`
                                            }}>
                                              {getStateStyle(t.state).label}
                                            </span>
                                          </td>
                                          <td data-label="Antenna">
                                            <span className="antenna-badge">
                                              <i className="fa fa-wifi" /> Ant {getExpectedAntenna(t.rid)}
                                            </span>
                                          </td>
                                          <td data-label="RSSI">
                                            {t.incomingData?.rssi != null && (
                                              <span className="rssi-badge" style={{
                                                backgroundColor: getRSSIColor(t.incomingData.rssi) + "20",
                                                color: getRSSIColor(t.incomingData.rssi),
                                                border: `1px solid ${getRSSIColor(t.incomingData.rssi)}`
                                              }}>
                                                {t.incomingData.rssi} dBm
                                              </span>
                                            )}
                                          </td>
                                          <td data-label="Timestamp" className="timestamp-cell">
                                            {formatDate(t.timestamp)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              {/* Misplaced — detected on wrong antenna */}
                              {room.misplaced?.tags?.length > 0 && (
                                <div className="system-scan-section">
                                  <div className="system-scan-section-title warning">
                                    <i className="fa fa-exchange" /> Misplaced ({room.misplaced.tags.length})
                                    <span className="system-scan-section-desc">Belongs to this room but detected on a different antenna</span>
                                  </div>
                                  <table className="tags-table compact">
                                    <thead>
                                      <tr>
                                        <th>TID</th>
                                        <th>Name</th>
                                        <th>EPC</th>
                                        <th>State</th>
                                        <th>Expected Ant</th>
                                        <th>Detected On</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {room.misplaced.tags.map((t, i) => (
                                        <tr key={`mp-${i}`} style={{ backgroundColor: "#FFFBEB" }}>
                                          <td data-label="TID">
                                            <span
                              className="tag-badge"
                              onClick={() => openTagDetails(t.tid)}
                              role="button"
                              tabIndex={t.tid ? 0 : -1}
                              onKeyDown={(e) => { if (e.key === "Enter") openTagDetails(t.tid); }}
                              title={t.tid ? "Open tag details" : undefined}
                              style={{ cursor: t.tid ? "pointer" : "default", textDecoration: t.tid ? "underline dotted" : "none", textUnderlineOffset: 3 }}
                            >{t.tid || "—"}</span>
                                          </td>
                                          <td data-label="Name">
                                            <span className="epc-cell fw-bold fs-6">{t.name || "UNNAMED"}</span>
                                          </td>
                                          <td data-label="EPC" className="epc-cell" style={{ fontSize: "12px" }}>{t.epc}</td>
                                          <td data-label="State">
                                            <span className="state-badge" style={{
                                              backgroundColor: getStateStyle(t.state).bg,
                                              color: getStateStyle(t.state).color,
                                              border: `1px solid ${getStateStyle(t.state).border}`
                                            }}>
                                              {getStateStyle(t.state).label}
                                            </span>
                                          </td>
                                          <td data-label="Expected Ant">
                                            <span className="antenna-badge">
                                              <i className="fa fa-wifi" /> Ant {getExpectedAntenna(t.rid)}
                                            </span>
                                          </td>
                                          <td data-label="Detected On">
                                            <span style={{
                                              display: "inline-flex", alignItems: "center", gap: "4px",
                                              padding: "2px 8px", borderRadius: "6px", fontSize: "12px", fontWeight: 600,
                                              backgroundColor: "#FEF3C7", color: "#92400E", border: "1px solid #F59E0B"
                                            }}>
                                              <i className="fa fa-exchange" />
                                              Reader {t.detectedReaderId || "?"} Ant {t.detectedAntenna || "?"} ({t.detectedRoomName})
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              {/* Missing — truly not detected anywhere */}
                              {room.missing?.tags?.length > 0 && (
                                <div className="system-scan-section">
                                  <div className="system-scan-section-title danger">
                                    <i className="fa fa-exclamation-circle" /> Missing ({room.missing.tags.length})
                                    <span className="system-scan-section-desc">In database but not detected on any antenna</span>
                                  </div>
                                  <table className="tags-table compact">
                                    <thead>
                                      <tr>
                                        <th>TID</th>
                                        <th>Name</th>
                                        <th>EPC</th>
                                        <th>State</th>
                                        <th>Antenna</th>
                                        <th>RSSI</th>
                                        <th>Last Seen</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {room.missing.tags.map((t, i) => (
                                        <tr key={`x-${i}`} style={{ backgroundColor: "#FEF2F2" }}>
                                          <td data-label="TID">
                                            <span
                              className="tag-badge"
                              onClick={() => openTagDetails(t.tid)}
                              role="button"
                              tabIndex={t.tid ? 0 : -1}
                              onKeyDown={(e) => { if (e.key === "Enter") openTagDetails(t.tid); }}
                              title={t.tid ? "Open tag details" : undefined}
                              style={{ cursor: t.tid ? "pointer" : "default", textDecoration: t.tid ? "underline dotted" : "none", textUnderlineOffset: 3 }}
                            >{t.tid || "—"}</span>
                                          </td>
                                          <td data-label="Name">
                                            <span className="epc-cell fw-bold fs-6">{t.name || "UNNAMED"}</span>
                                          </td>
                                          <td data-label="EPC" className="epc-cell" style={{ fontSize: "12px" }}>{t.epc}</td>
                                          <td data-label="State">
                                            <span className="state-badge" style={{
                                              backgroundColor: getStateStyle(t.state).bg,
                                              color: getStateStyle(t.state).color,
                                              border: `1px solid ${getStateStyle(t.state).border}`
                                            }}>
                                              {getStateStyle(t.state).label}
                                            </span>
                                          </td>
                                          <td data-label="Antenna">
                                            <span className="antenna-badge">
                                              <i className="fa fa-wifi" /> Ant {getExpectedAntenna(t.rid)}
                                            </span>
                                          </td>
                                          <td data-label="RSSI">
                                            {t.rssi != null ? (
                                              <span className="rssi-badge" style={{
                                                backgroundColor: getRSSIColor(t.rssi) + "20",
                                                color: getRSSIColor(t.rssi),
                                                border: `1px solid ${getRSSIColor(t.rssi)}`
                                              }}>
                                                {t.rssi} dBm
                                              </span>
                                            ) : "—"}
                                          </td>
                                          <td data-label="Last Seen" className="timestamp-cell">
                                            {formatDate(t.timestamp)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              {/* Extra */}
                              {room.extra?.tags?.length > 0 && (
                                <div className="system-scan-section">
                                  <div className="system-scan-section-title warning">
                                    <i className="fa fa-plus-circle" /> Extra ({room.extra.tags.length})
                                    <span className="system-scan-section-desc">Found in scan but not assigned to this room</span>
                                  </div>
                                  <table className="tags-table compact">
                                    <thead>
                                      <tr>
                                        <th>TID</th>
                                        <th>Name</th>
                                        <th>EPC</th>
                                        <th>State</th>
                                        <th>Antenna</th>
                                        <th>Belongs To</th>
                                        <th>RSSI (scan)</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {room.extra.tags.map((t, i) => {
                                        const isRegistered = t.registered !== false;
                                        return (
                                          <tr key={`e-${i}`} style={{ backgroundColor: isRegistered ? "#FFFBEB" : "#F5F3FF" }}>
                                            <td data-label="TID">
                                              <span
                              className="tag-badge"
                              onClick={() => openTagDetails(t.tid)}
                              role="button"
                              tabIndex={t.tid ? 0 : -1}
                              onKeyDown={(e) => { if (e.key === "Enter") openTagDetails(t.tid); }}
                              title={t.tid ? "Open tag details" : undefined}
                              style={{ cursor: t.tid ? "pointer" : "default", textDecoration: t.tid ? "underline dotted" : "none", textUnderlineOffset: 3 }}
                            >{t.tid || "—"}</span>
                                            </td>
                                            <td data-label="Name">
                                              {isRegistered ? (
                                                <span className="epc-cell fw-bold fs-6">{t.name || "UNNAMED"}</span>
                                              ) : (
                                                <span style={{ color: "#7C3AED", fontStyle: "italic", fontWeight: 500 }}>
                                                  <i className="fa fa-exclamation-triangle" style={{ marginRight: 5 }} />
                                                  Not in Database
                                                </span>
                                              )}
                                            </td>
                                            <td data-label="EPC" className="epc-cell" style={{ fontSize: "12px" }}>{t.epc}</td>
                                            <td data-label="State">
                                              {isRegistered && t.state ? (
                                                <span className="state-badge" style={{
                                                  backgroundColor: getStateStyle(t.state).bg,
                                                  color: getStateStyle(t.state).color,
                                                  border: `1px solid ${getStateStyle(t.state).border}`
                                                }}>
                                                  {getStateStyle(t.state).label}
                                                </span>
                                              ) : (
                                                <span style={{ color: "var(--text-tertiary)" }}>—</span>
                                              )}
                                            </td>
                                            <td data-label="Antenna">
                                              <span className="antenna-badge">
                                                <i className="fa fa-wifi" /> Ant {t.antenna || "?"}
                                              </span>
                                            </td>
                                            <td data-label="Belongs To">
                                              {isRegistered ? (
                                                <span style={{
                                                  display: "inline-flex", alignItems: "center", gap: "4px",
                                                  padding: "2px 8px", borderRadius: "6px", fontSize: "12px", fontWeight: 600,
                                                  backgroundColor: "#DBEAFE", color: "#1E40AF", border: "1px solid #3B82F6"
                                                }}>
                                                  <i className="fa fa-arrow-right" />
                                                  {t.belongsToRoomName || getRoomName(t.dbRoomId || t.rid)}
                                                </span>
                                              ) : (
                                                <span style={{
                                                  display: "inline-flex", alignItems: "center", gap: "4px",
                                                  padding: "2px 8px", borderRadius: "6px", fontSize: "12px", fontWeight: 600,
                                                  backgroundColor: "#F3E8FF", color: "#7C3AED", border: "1px solid #A78BFA"
                                                }}>
                                                  <i className="fa fa-question-circle" />
                                                  Unregistered
                                                </span>
                                              )}
                                            </td>
                                            <td data-label="RSSI">
                                              {t.rssi != null ? (
                                                <span className="rssi-badge" style={{
                                                  backgroundColor: getRSSIColor(t.rssi) + "20",
                                                  color: getRSSIColor(t.rssi),
                                                  border: `1px solid ${getRSSIColor(t.rssi)}`
                                                }}>
                                                  {t.rssi} dBm
                                                </span>
                                              ) : "—"}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              {/* All clear */}
                              {(room.alreadyExists?.tags?.length || 0) === 0 &&
                               (room.missing?.tags?.length || 0) === 0 &&
                               (room.misplaced?.tags?.length || 0) === 0 &&
                               (room.extra?.tags?.length || 0) === 0 && (
                                <div className="empty-state" style={{ padding: "24px" }}>
                                  <i className="fa fa-inbox empty-state-icon" />
                                  <p>No tags found in scan or database for this room</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {renderPager(systemRoomsCurrentPage, systemRoomsTotalPages, setSystemRoomsPage)}
                </div>
              </>
            )}

            {/* Empty state before first scan */}
            {!systemScanResults && !systemScanning && (
              <div className="table-section">
                <div className="table-container">
                  <div className="empty-state">
                    <i className="fa fa-building empty-state-icon" />
                    <p>Press "Scan System" to scan all rooms and compare against the database</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ═══════════════════ GATE / THEFT DETECTION TAB ═══════════════════ */}
        {activeTab === "theft" && (
          <>
            {/* Per-Reader Gate Monitor Controls */}
            <div className="table-section">
              <div className="section-header">
                <h2 className="section-title">Gate Antenna Monitors</h2>
              </div>
              <div className="table-container">
                {readers.filter((r) => r.connected).length === 0 ? (
                  <div className="empty-state">
                    <i className="fa fa-unlink empty-state-icon" />
                    <p>No connected readers. Connect a reader first.</p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "16px" }}>
                    {readers.filter((r) => r.connected).map((r) => {
                      const readerTheftAnts = antennas.filter(
                        (a) => a.readerId === r.readerId && (a.usage === "GATE" || a.usage === "THEFT" || a.usage === "CHECKING")
                      );
                      const isActive = theftMonitors.includes(r.readerId);
                      if (readerTheftAnts.length === 0) return null;
                      return (
                        <div
                          key={r.readerId}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px 16px",
                            borderRadius: "10px",
                            border: `1px solid ${isActive ? "#86EFAC" : "#E2E8F0"}`,
                            backgroundColor: isActive ? "#F0FDF4" : "var(--bg-secondary)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <i
                              className="fa fa-shield"
                              style={{
                                fontSize: "22px",
                                color: isActive ? "#15803D" : "#94A3B8",
                              }}
                            />
                            <div>
                              <div style={{ fontWeight: 700, fontSize: "14px" }}>{r.readerId}</div>
                              <div style={{ fontSize: "12px", color: "var(--text-tertiary)" }}>
                                {r.ip}:{r.port} — {readerTheftAnts.length} gate antenna(s):
                                {" "}{readerTheftAnts.map((a) => `Ant ${a.antennaNumber}${a.name ? ` (${a.name})` : ""}`).join(", ")}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <span
                              className="state-badge"
                              style={{
                                backgroundColor: isActive ? "#DCFCE7" : "#FEE2E2",
                                color: isActive ? "#15803D" : "#DC2626",
                                border: `1px solid ${isActive ? "#86EFAC" : "#FCA5A5"}`,
                                fontSize: "11px",
                              }}
                            >
                              {isActive ? "Active" : "Paused"}
                            </span>
                            <button
                              onClick={() => handleToggleTheftMonitor(r.readerId)}
                              disabled={togglingTheft === r.readerId}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "7px 16px",
                                borderRadius: "8px",
                                border: "1px solid",
                                cursor: "pointer",
                                fontWeight: 600,
                                fontSize: "13px",
                                borderColor: isActive ? "#FCA5A5" : "#86EFAC",
                                backgroundColor: isActive ? "#FEE2E2" : "#DCFCE7",
                                color: isActive ? "#DC2626" : "#15803D",
                              }}
                            >
                              {togglingTheft === r.readerId ? (
                                <i className="fa fa-spinner fa-spin" />
                              ) : isActive ? (
                                <><i className="fa fa-pause" /> Pause</>
                              ) : (
                                <><i className="fa fa-play" /> Resume</>
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {readers.filter((r) => r.connected).every(
                      (r) => antennas.filter((a) => a.readerId === r.readerId && (a.usage === "GATE" || a.usage === "THEFT" || a.usage === "CHECKING")).length === 0
                    ) && (
                      <div className="empty-state">
                        <i className="fa fa-shield empty-state-icon" />
                        <p>No GATE antennas assigned to any connected reader</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Theft Events Log */}
            <div className="table-section">
              <div className="section-header">
                <h2 className="section-title">
                  Detection Log
                  {theftEvents.length > 0 && (
                    <span style={{ fontSize: "13px", fontWeight: 400, color: "var(--text-tertiary)", marginLeft: "8px" }}>
                      ({theftEvents.length} events)
                    </span>
                  )}
                </h2>
                <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                  <button
                    onClick={() => setTheftFilterAlarm(!theftFilterAlarm)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "5px 12px",
                      borderRadius: "6px",
                      border: `1px solid ${theftFilterAlarm ? "#FCA5A5" : "#CBD5E1"}`,
                      backgroundColor: theftFilterAlarm ? "#FEE2E2" : "var(--bg-secondary)",
                      color: theftFilterAlarm ? "#DC2626" : "var(--text-secondary)",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "12px",
                    }}
                  >
                    <i className={`fa ${theftFilterAlarm ? "fa-check-square-o" : "fa-square-o"}`} />
                    Alarms Only
                  </button>
                  {selectedTheftEvents.size > 0 && (
                    <button
                      onClick={handleDeleteSelectedTheftEvents}
                      disabled={deletingTheftEvents}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "5px 12px",
                        borderRadius: "6px",
                        border: "1px solid #FCA5A5",
                        backgroundColor: "#FEE2E2",
                        color: "#DC2626",
                        cursor: deletingTheftEvents ? "not-allowed" : "pointer",
                        fontWeight: 600,
                        fontSize: "12px",
                        opacity: deletingTheftEvents ? 0.6 : 1,
                      }}
                    >
                      {deletingTheftEvents ? (
                        <i className="fa fa-spinner fa-spin" />
                      ) : (
                        <i className="fa fa-trash" />
                      )}
                      Delete Selected ({selectedTheftEvents.size})
                    </button>
                  )}
                  {theftEvents.length > 0 && (
                    <button
                      onClick={handleClearTheftEvents}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "5px 12px",
                        borderRadius: "6px",
                        border: "1px solid #CBD5E1",
                        backgroundColor: "var(--bg-secondary)",
                        color: "var(--text-secondary)",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "12px",
                      }}
                    >
                      <i className="fa fa-trash" />
                      Clear All
                    </button>
                  )}
                </div>
              </div>
              <div className="table-container">
                {theftEventsLoading ? (
                  <div className="loading-state">
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: "32px", color: "var(--text-tertiary)" }} />
                    <p>Loading events...</p>
                  </div>
                ) : theftEvents.length === 0 ? (
                  <div className="empty-state">
                    <i className="fa fa-check-circle empty-state-icon" style={{ color: "#15803D" }} />
                    <p>{theftFilterAlarm ? "No alarm events recorded" : "No theft detection events yet"}</p>
                  </div>
                ) : (
                  <table className="tags-table">
                    <thead>
                      <tr>
                        <th style={{ width: "36px", textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={allSelectedTheftOnPage}
                            onChange={toggleSelectAllTheftEvents}
                            style={{ cursor: "pointer", accentColor: "var(--primary)" }}
                            title={allSelectedTheftOnPage ? "Deselect all (page)" : "Select all (page)"}
                          />
                        </th>
                        <th>Time</th>
                        <th>Status</th>
                        <th>EPC</th>
                        <th>Name</th>
                        <th>State</th>
                        <th>Reader</th>
                        <th>Antenna</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedTheftEvents.map((evt, idx) => {
                        const isSelected = selectedTheftEvents.has(evt._id);
                        return (
                          <tr
                            key={evt._id || `${evt.epc}-${evt.timestamp}-${(theftCurrentPage - 1) * THEFT_EVENTS_PAGE_SIZE + idx}`}
                            style={{
                              backgroundColor: isSelected ? "#EFF6FF" : evt.alarm ? "#FEF2F2" : undefined,
                              cursor: "pointer",
                            }}
                            onClick={() => toggleTheftEventSelection(evt._id)}
                          >
                            <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleTheftEventSelection(evt._id)}
                                style={{ cursor: "pointer", accentColor: "var(--primary)" }}
                              />
                            </td>
                            <td className="timestamp-cell">
                              {formatDate(evt.timestamp)}
                            </td>
                            <td>
                              {evt.alarm ? (
                                <span
                                  className="state-badge"
                                  style={{
                                    backgroundColor: "#FEE2E2",
                                    color: "#DC2626",
                                    border: "1px solid #FCA5A5",
                                    fontWeight: 700,
                                  }}
                                >
                                  <i className="fa fa-exclamation-triangle" style={{ marginRight: "4px" }} />
                                  ALARM
                                </span>
                              ) : (
                                <span
                                  className="state-badge"
                                  style={{
                                    backgroundColor: "#DCFCE7",
                                    color: "#15803D",
                                    border: "1px solid #86EFAC",
                                  }}
                                >
                                  OK
                                </span>
                              )}
                            </td>
                            <td className="epc-cell" style={{ fontSize: "12px" }}>{evt.epc}</td>
                            <td>
                              <span className="epc-cell fw-bold">{evt.name || "—"}</span>
                            </td>
                            <td>
                              <span
                                className="state-badge"
                                style={{
                                  backgroundColor: getStateStyle(evt.state).bg,
                                  color: getStateStyle(evt.state).color,
                                  border: `1px solid ${getStateStyle(evt.state).border}`,
                                }}
                              >
                                {getStateStyle(evt.state).label}
                              </span>
                            </td>
                            <td>
                              <span className="tag-badge">{evt.readerId}</span>
                            </td>
                            <td>
                              <span className="antenna-badge">
                                <i className="fa fa-wifi" /> Ant {evt.antenna}
                              </span>
                            </td>
                            <td style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
                              {evt.action}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                {!theftEventsLoading && theftEvents.length > 0 && renderPager(theftCurrentPage, theftTotalPages, setTheftEventsPage)}
              </div>
            </div>
          </>
        )}

      </div>

      {/* ═══════════════════ ADD/EDIT READER MODAL ═══════════════════ */}
      {showReaderModal && (
        <div className="modal-overlay" onClick={() => { setShowReaderModal(false); setEditingReader(null); }}>
          <div className="modal-container tag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle tag-icon-bg">
                <i className="fa fa-server" />
              </div>
              <div className="modal-title-section">
                <span className="modal-title">{editingReader ? "Edit Reader" : "Add Reader"}</span>
                <span className="modal-subtitle">{editingReader ? "Update reader configuration" : "Register a new RFID reader"}</span>
              </div>
              <div className="modal-close-btn" onClick={() => { setShowReaderModal(false); setEditingReader(null); }}>
                <i className="fa fa-times" />
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Reader ID</span>
                  <span className="form-label-required">*</span>
                </div>
                <TextField
                  placeholder="e.g. R1"
                  value={readerForm.readerId}
                  onChange={(e) => setReaderForm({ ...readerForm, readerId: e.target.value })}
                  disabled={!!editingReader}
                />
              </div>
              <div className="form-row-group">
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">IP Address</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <TextField
                    placeholder="e.g. 192.168.1.100"
                    value={readerForm.ip}
                    onChange={(e) => setReaderForm({ ...readerForm, ip: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Port</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <TextField
                    type="number"
                    placeholder="e.g. 9090"
                    value={readerForm.port}
                    onChange={(e) => setReaderForm({ ...readerForm, port: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row-group">
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Antenna Count</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <TextField
                    type="number"
                    placeholder="1-16"
                    value={readerForm.antennaCount}
                    onChange={(e) => setReaderForm({ ...readerForm, antennaCount: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Warehouse</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <Dropdown
                    options={warehouseOptions}
                    value={readerForm.warehouseLocation}
                    onChange={(val) => setReaderForm({ ...readerForm, warehouseLocation: val })}
                    placeholder="Select Warehouse"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => { setShowReaderModal(false); setEditingReader(null); }}
              >
                <i className="fa fa-times" />
                <span>Cancel</span>
              </div>
              <div className="modal-action-btn primary-btn" onClick={handleAddReader}>
                <i className={`fa ${editingReader ? "fa-save" : "fa-plus"}`} />
                <span>{editingReader ? "Save Changes" : "Add Reader"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════ ASSIGN/EDIT ANTENNA MODAL ═══════════════════ */}
      {showAntennaModal && (
        <div className="modal-overlay" onClick={() => { setShowAntennaModal(false); setEditingAntenna(null); }}>
          <div className="modal-container tag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle room-icon-bg">
                <i className="fa fa-wifi" />
              </div>
              <div className="modal-title-section">
                <span className="modal-title">{editingAntenna ? "Edit Antenna" : "Assign Antenna"}</span>
                <span className="modal-subtitle">{editingAntenna ? "Update antenna configuration" : "Configure antenna usage for a reader"}</span>
              </div>
              <div className="modal-close-btn" onClick={() => { setShowAntennaModal(false); setEditingAntenna(null); }}>
                <i className="fa fa-times" />
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Reader</span>
                  <span className="form-label-required">*</span>
                </div>
                <Dropdown
                  options={readerOptions}
                  value={antennaForm.readerId}
                  onChange={(val) => setAntennaForm({ ...antennaForm, readerId: val })}
                  placeholder="Select Reader"
                  disabled={!!editingAntenna}
                />
              </div>
              <div className="form-row-group">
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Antenna Number</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <TextField
                    type="number"
                    placeholder="e.g. 1"
                    value={antennaForm.antennaNumber}
                    onChange={(e) => setAntennaForm({ ...antennaForm, antennaNumber: e.target.value })}
                    disabled={!!editingAntenna}
                  />
                </div>
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Usage</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <Dropdown
                    options={[
                      { label: "GATE (out/in + alerts)", value: "GATE" },
                      { label: "SCAN (partition inventory)", value: "SCAN" },
                      { label: "THEFT (legacy)", value: "THEFT" },
                      { label: "CHECKING (legacy)", value: "CHECKING" },
                    ]}
                    value={antennaForm.usage}
                    onChange={(val) => setAntennaForm({
                      ...antennaForm,
                      usage: val,
                      ...(val !== "SCAN" ? { roomId: "" } : {}),
                      ...(val === "SCAN" ? { warehouseId: "" } : {}),
                    })}
                    placeholder="Select Usage"
                  />
                </div>
              </div>
              <div className="form-row-group">
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Name</span>
                  </div>
                  <TextField
                    placeholder={
                      (antennaForm.usage === "GATE" || antennaForm.usage === "THEFT" || antennaForm.usage === "CHECKING")
                        ? "e.g. Main Gate" : "e.g. Shelf Scanner"
                    }
                    value={antennaForm.name}
                    onChange={(e) => setAntennaForm({ ...antennaForm, name: e.target.value })}
                  />
                </div>
                {antennaForm.usage === "SCAN" && (
                  <div className="form-field">
                    <div
                      className="form-label-wrapper"
                      style={{ alignItems: "baseline", gap: 8, flexWrap: "wrap" }}
                    >
                      <span className="form-label">Partition</span>
                      <span className="form-label-required">*</span>
                      <span style={{ fontSize: 11, color: "var(--text-tertiary)", fontStyle: "italic" }}>
                        in the reader&apos;s warehouse
                      </span>
                    </div>
                    <Dropdown
                      options={antennaRoomOptions}
                      value={antennaForm.roomId}
                      onChange={(val) => setAntennaForm({ ...antennaForm, roomId: val })}
                      placeholder={
                        !antennaForm.readerId
                          ? "Select a reader first"
                          : antennaRoomOptions.length === 0
                            ? "No partitions available"
                            : "Select Partition"
                      }
                      disabled={antennaRoomOptions.length === 0}
                    />
                    {antennaForm.readerId && antennaRoomOptions.length === 0 && (
                      <span style={{ fontSize: 11, color: "#B45309", marginTop: 6, display: "block" }}>
                        {!antennaFormWarehouseId
                          ? "This reader has no warehouse assigned — set one on the reader first."
                          : "This reader's warehouse has no partitions yet — add one on the Warehouses page."}
                      </span>
                    )}
                  </div>
                )}
                {(antennaForm.usage === "GATE" || antennaForm.usage === "THEFT" || antennaForm.usage === "CHECKING") && (() => {
                  // GATE antennas always inherit the reader's warehouse — no
                  // picker. If the reader doesn't have one, flag the problem
                  // and nudge the operator to fix the reader instead.
                  const reader = readers.find((r) => r.readerId === antennaForm.readerId);
                  const readerWhId = reader?.warehouseId || reader?.warehouseLocation || "";
                  const readerWh = warehouses.find((w) => String(w._id) === String(readerWhId));
                  const hasWarehouse = !!readerWh;
                  const baseBoxStyle = {
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    minHeight: 40,
                    padding: "10px 12px",
                    borderRadius: "var(--radius-md)",
                    fontSize: 13,
                    lineHeight: 1.4,
                    boxSizing: "border-box",
                  };
                  return (
                    <div className="form-field">
                      <div
                        className="form-label-wrapper"
                        style={{ alignItems: "baseline", gap: 8, flexWrap: "wrap" }}
                      >
                        <span className="form-label">Warehouse</span>
                        <span style={{ fontSize: 11, color: "var(--text-tertiary)", fontStyle: "italic" }}>
                          inherited from reader
                        </span>
                      </div>
                      <div
                        style={{
                          ...baseBoxStyle,
                          border: `1px solid ${hasWarehouse ? "var(--border-primary)" : "#FCA5A5"}`,
                          background: hasWarehouse ? "var(--bg-secondary)" : "#FEF2F2",
                          color: hasWarehouse ? "var(--text-primary)" : "#991B1B",
                        }}
                      >
                        <i
                          className={`fa ${hasWarehouse ? "fa-building" : "fa-exclamation-triangle"}`}
                          aria-hidden
                          style={{ marginTop: 2, flexShrink: 0, color: hasWarehouse ? "var(--text-tertiary)" : "#B91C1C" }}
                        />
                        <span style={{ flex: 1 }}>
                          {hasWarehouse
                            ? readerWh.name
                            : (antennaForm.readerId
                                ? "Selected reader has no warehouse assigned — set one on the reader first."
                                : "Select a reader above to see its warehouse.")}
                        </span>
                      </div>
                    </div>
                  );
                })()}
                {(antennaForm.usage === "GATE" || antennaForm.usage === "THEFT" || antennaForm.usage === "CHECKING") && (
                  <div className="form-field">
                    <div className="form-label-wrapper">
                      <span className="form-label">Alarm Output (GPO)</span>
                      <span className="form-label-required">*</span>
                    </div>
                    <Dropdown
                      options={[
                        { label: "GPO1", value: "1" },
                        { label: "GPO2 (default)", value: "2" },
                        { label: "GPO3", value: "3" },
                        { label: "GPO4", value: "4" },
                      ]}
                      value={String(antennaForm.gpoPin || "2")}
                      onChange={(val) => setAntennaForm({ ...antennaForm, gpoPin: val })}
                      placeholder="Select GPO"
                    />
                    <div style={{ fontSize: "11px", color: "var(--text-tertiary)", marginTop: "2px" }}>
                      Dry-contact relay this gate closes when it alarms — wire the
                      siren / strobe across the reader&apos;s matching GPO terminals.
                      Each gate on a reader can use its own output.
                    </div>
                  </div>
                )}
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Power (Distance)</span>
                    <span style={{ fontSize: "12px", color: "var(--text-tertiary)", marginLeft: "auto" }}>
                      {antennaForm.power} dBm
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ fontSize: "11px", color: "var(--text-tertiary)", minWidth: "28px" }}>0</span>
                    <input
                      type="range"
                      min="0"
                      max="33"
                      step="1"
                      value={antennaForm.power}
                      onChange={(e) => setAntennaForm({ ...antennaForm, power: e.target.value })}
                      style={{ flex: 1, accentColor: "var(--primary)" }}
                    />
                    <span style={{ fontSize: "11px", color: "var(--text-tertiary)", minWidth: "28px" }}>33</span>
                  </div>
                  <div style={{ fontSize: "11px", color: "var(--text-tertiary)", marginTop: "2px" }}>
                    Higher power = longer read distance. Default: 30 dBm
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => { setShowAntennaModal(false); setEditingAntenna(null); }}
              >
                <i className="fa fa-times" />
                <span>Cancel</span>
              </div>
              <div className="modal-action-btn primary-btn" onClick={handleAssignAntenna}>
                <i className={`fa ${editingAntenna ? "fa-save" : "fa-check"}`} />
                <span>{editingAntenna ? "Save Changes" : "Assign"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════ ADD/EDIT DESKTOP DEVICE MODAL ═══════════════════ */}
      {showDesktopModal && (
        <div className="modal-overlay" onClick={handleCancelDesktopEdit}>
          <div className="modal-container tag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle tag-icon-bg">
                <i className="fa fa-usb" />
              </div>
              <div className="modal-title-section">
                <span className="modal-title">{editingDesktopDevice ? "Edit Desktop POS Device" : "Add Desktop POS Device"}</span>
                <span className="modal-subtitle">{editingDesktopDevice ? "Update desktop reader settings" : "Configure a desktop reader for cashier POS"}</span>
              </div>
              <div className="modal-close-btn" onClick={handleCancelDesktopEdit}>
                <i className="fa fa-times" />
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-row-group">
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Device ID</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <TextField
                    placeholder="e.g. POS_DESK_1"
                    value={desktopForm.deviceId}
                    onChange={(e) => setDesktopForm((p) => ({ ...p, deviceId: e.target.value }))}
                    disabled={!!editingDesktopDevice}
                  />
                </div>
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Display Name</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <TextField
                    placeholder="e.g. Cashier Counter 1"
                    value={desktopForm.name}
                    onChange={(e) => setDesktopForm((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-row-group">
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Connection Type</span>
                    <span className="form-label-required">*</span>
                  </div>
                  <Dropdown
                    options={[
                      { label: "USB HID", value: "USB_HID" },
                      { label: "USB to RS232 (COM)", value: "SERIAL" },
                    ]}
                    value={desktopForm.connectionType}
                    onChange={(v) => setDesktopForm((p) => ({ ...p, connectionType: v }))}
                    placeholder="Connection Type"
                  />
                </div>
                {desktopForm.connectionType === "USB_HID" ? (
                  <div className="form-field">
                    <div className="form-label-wrapper">
                      <span className="form-label">USB HID Path</span>
                      <span className="form-label-required">*</span>
                    </div>
                    <Dropdown
                      options={[
                        { label: "Select USB HID Path", value: "" },
                        ...desktopUsbPaths.map((p) => ({ label: p, value: p })),
                      ]}
                      value={desktopForm.usbPath}
                      onChange={(v) => setDesktopForm((p) => ({ ...p, usbPath: v }))}
                      placeholder="Select USB HID Path"
                    />
                  </div>
                ) : (
                  <div className="form-field">
                    <div className="form-label-wrapper">
                      <span className="form-label">COM Port</span>
                      <span className="form-label-required">*</span>
                    </div>
                    <Dropdown
                      options={[
                        { label: "Select COM Port", value: "" },
                        ...desktopSerialPorts.map((p) => ({ label: `${p.port} — ${p.description}`, value: p.port })),
                      ]}
                      value={desktopForm.serialPort}
                      onChange={(v) => setDesktopForm((p) => ({ ...p, serialPort: v }))}
                      placeholder="Select COM Port"
                    />
                  </div>
                )}
              </div>

              <div className="form-row-group">
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Baud Rate</span>
                  </div>
                  <Dropdown
                    options={[
                      { label: "115200", value: "115200" },
                      { label: "57600", value: "57600" },
                      { label: "38400", value: "38400" },
                      { label: "9600", value: "9600" },
                    ]}
                    value={desktopForm.baudRate}
                    onChange={(v) => setDesktopForm((p) => ({ ...p, baudRate: v }))}
                    placeholder="Baud Rate"
                    disabled={desktopForm.connectionType !== "SERIAL"}
                  />
                </div>
                <div className="form-field">
                  <div className="form-label-wrapper">
                    <span className="form-label">Power (Distance)</span>
                    <span style={{ fontSize: "12px", color: "var(--text-tertiary)", marginLeft: "auto" }}>
                      {desktopForm.power} dBm
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ fontSize: "11px", color: "var(--text-tertiary)", minWidth: "28px" }}>5</span>
                    <input
                      type="range"
                      min="5"
                      max="33"
                      step="1"
                      value={desktopForm.power}
                      onChange={(e) => setDesktopForm((p) => ({ ...p, power: e.target.value }))}
                      style={{ flex: 1, accentColor: "var(--primary)" }}
                    />
                    <span style={{ fontSize: "11px", color: "var(--text-tertiary)", minWidth: "28px" }}>33</span>
                  </div>
                  <div style={{ fontSize: "11px", color: "var(--text-tertiary)", marginTop: "2px" }}>
                    Higher power = longer read distance. Default: 30 dBm
                  </div>
                </div>
              </div>

              <div className="dfcc gap-2" style={{ marginTop: 8 }}>
                <Button
                  value="Refresh USB Paths"
                  iconLeft="fa-refresh"
                  variant="outline"
                  size="sm"
                  onClick={fetchDesktopUsbPaths}
                />
                <Button
                  value="Refresh COM Ports"
                  iconLeft="fa-refresh"
                  variant="outline"
                  size="sm"
                  onClick={fetchDesktopSerialPorts}
                />
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div className="modal-action-btn cancel-btn" onClick={handleCancelDesktopEdit}>
                <i className="fa fa-times" />
                <span>Cancel</span>
              </div>
              <div className="modal-action-btn primary-btn" onClick={handleSaveDesktopDevice}>
                <i className={`fa ${desktopSaving ? "fa-spinner fa-spin" : (editingDesktopDevice ? "fa-save" : "fa-plus")}`} />
                <span>{desktopSaving ? "Saving..." : (editingDesktopDevice ? "Save Changes" : "Add Device")}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════ CONFIRM DELETE MODAL ═══════════════════ */}
      {confirmModal && (
        <div className="modal-overlay" onClick={() => setConfirmModal(null)}>
          <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper delete-header">
              <div className="modal-icon-circle delete-icon-bg">
                <i className="fa fa-exclamation-triangle" />
              </div>
              <div className="modal-title-section">
                <span className="modal-title delete-title">{confirmModal.title}</span>
                <span className="modal-subtitle delete-subtitle">
                  This action is permanent and cannot be undone
                </span>
              </div>
              <div className="modal-close-btn delete-close" onClick={() => setConfirmModal(null)}>
                <i className="fa fa-times" />
              </div>
            </div>

            <div className="modal-body-wrapper delete-body">
              <div className="delete-message-wrapper">
                <div className="delete-message-icon">
                  <i className={`fa ${confirmModal.icon || "fa-info-circle"}`} />
                </div>
                <div className="delete-message-content">
                  <span className="delete-message-primary">
                    {confirmModal.message}
                  </span>
                  <span className="delete-message-secondary">
                    {confirmModal.detail}
                  </span>
                </div>
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => setConfirmModal(null)}
              >
                <i className="fa fa-times" />
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn delete-btn"
                onClick={confirmModal.onConfirm}
              >
                <i className="fa fa-trash" />
                <span>{confirmModal.confirmLabel}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Operators;
